import { signal, computed } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { ConfirmationService } from 'primeng/api';
import { ToastService } from '../../../../toast/toast.service';
import { ExecutionService } from '../services/execution.service';
import { StepsService } from '../../../steps/services/steps.service';
import { ExecutionResultService } from '../../../../services/execution-result.service';
import { TestExecutorService } from '../../../../services/test-executor.service';
import { StepPlaceholderService } from '../../../../services/step-placeholder.service';
import { SessionPoolService } from '../../../../services/session-pool.service';
import { Table } from 'primeng/table';
import { FuntionalAreaService } from '../../../test-suite/services/test-suite.service';
import { AuthService } from '../../../../auth/login/services/auth.service';
import { ReportApiService, ReportPayload } from '../../../report/services/report-api.service';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';

export interface ReferenceData {
    id: string;
    referenceValue: string;
    referenceKey?: string;
}

export interface FunctionalArea extends ReferenceData {
    name?: string;
    description?: string;
    clientPackageMappingId?: string | null;
    clientPackageMapping?: {
        id: string;
        clientId: string;
        packageId: string;
        startDate?: string;
        expirationDate?: string;
        package: {
            id: string;
            packageName: string;
            packageDescription?: string;
            isActive?: boolean;
        };
        client?: {
            id: string;
            name: string;
            description?: string;
            isActive?: boolean;
        };
    };
    // For template scenarios (when clientPackageMappingId is null)
    package?: {
        id: string;
        packageName: string;
        packageDescription?: string;
        isActive?: boolean;
    };
}

export interface Scenario {
    id: string;
    scenarioId: string;
    scenarioName: string;
    scenarioDescription: string;
    selected: boolean;
    packageId: string;
    functionalAreaId: string;
    testTypeId: string;
    package?: { id: string; packageName: string };
    functionalArea?: FunctionalArea;
    testType?: ReferenceData;
}

export class ExecutionsViewModel {
    /**
     * Resolve authProfileId from scenario — checks authProfileId first,
     * falls back to first ID in authProfileInfo.authProfileIds (profile group)
     */
    private resolveAuthProfileId(sc: any): string | null {
        if (sc?.authProfileId) return sc.authProfileId;
        if (sc?.authProfileInfo) {
            try {
                const info = typeof sc.authProfileInfo === 'string' ? JSON.parse(sc.authProfileInfo) : sc.authProfileInfo;
                if (info?.authProfileIds?.length > 0) return info.authProfileIds[0];
            } catch { /* ignore */ }
        }
        return null;
    }

    /**
     * Two-level session policy resolution for a scenario.
     * Level 1: Use explicit sessionPolicy (FRESH/REUSE).
     * Level 2: If null, inspect first steps — login pattern = FRESH, else REUSE.
     */
    private async resolveEffectiveSessionPolicy(scenarioId: string, sessionPolicy: string | null): Promise<'FRESH' | 'REUSE'> {
        // Level 1: Explicit policy wins
        if (sessionPolicy === 'FRESH') return 'FRESH';
        if (sessionPolicy === 'REUSE') return 'REUSE';

        // Level 2: Auto-detect from first steps
        try {
            const steps: any[] = await new Promise((resolve, reject) => {
                this.stepsService.getStepsByScenario(scenarioId).subscribe({
                    next: (data: any) => resolve(Array.isArray(data) ? data : (data?.data || [])),
                    error: () => resolve([])
                });
            });
            const first3 = steps
                .filter((s: any) => s.isActive !== false)
                .sort((a: any, b: any) => (a.stepId || 0) - (b.stepId || 0))
                .slice(0, 3);
            if (this.hasLoginSteps(first3)) return 'FRESH';
        } catch { /* default to REUSE on error */ }
        return 'REUSE';
    }

    private hasLoginSteps(steps: any[]): boolean {
        const usernamePattern = /enter\s+(?:the\s+)?(?:user\s*name|username|user\s*id)\s+(?:as|in|into)\s+/i;
        const usernamePattern2 = /enter\s+.+\s+(?:in|into|as)\s+(?:the\s+)?(?:user\s*name|username|user\s*id|user)/i;
        const passwordPattern = /enter\s+(?:the\s+)?password\s+(?:as|in|into)\s+/i;
        const passwordPattern2 = /enter\s+.+\s+(?:in|into|as)\s+(?:the\s+)?password/i;
        const descriptions = steps.map(s => s.stepDescription || '').filter(Boolean);
        const hasUsername = descriptions.some(d => usernamePattern.test(d) || usernamePattern2.test(d));
        const hasPassword = descriptions.some(d => passwordPattern.test(d) || passwordPattern2.test(d));
        return hasUsername && hasPassword;
    }

    private fb: FormBuilder;

    scenarioDialog: boolean = false;
    scenarios = signal<Scenario[]>([]);
    clientFilterOptions = signal<{ label: string, value: string }[]>([]);
    isSystemUser: boolean = false;
    packageFilterOptions = computed(() => {
        const packages = this.allPackages();
        return packages.map(pkg => ({
            label: pkg.packageName,
            value: pkg.packageName
        }));
    });
    testSuiteFilterOptions = signal<{ label: string, value: string }[]>([]);
    allPackages = signal<any[]>([]);
    selectedClientFilter = signal<string>('');
    selectedPackageFilter = signal<string>('');
    selectedTestSuiteFilter = signal<string>('');
    selectedFunctionalAreaFilter = signal<string>('');
    selectedTestTypeFilter = signal<string[]>([]);
    filterTestType = signal<string[]>([]); // Add multiselect test type array
    allScenarios = signal<Scenario[]>([]);
    scenarioPage = signal<number>(1);
    scenarioPageSize = signal<number>(10);
    scenarioTotal = signal<number>(0);
    scenarioFirst = signal<number>(0);
    scenarioSortField = signal<string>('');
    scenarioSortOrder = signal<number>(1);
    selectedScenario: Scenario | null = null;
    isClientDropdownDisabled = signal<boolean>(false); // Added to control dropdown state
    
    clientTenants = signal<any[]>([]);
    tenantOptions = computed(() => {
        return this.clientTenants().map(t => ({
            ...t,
            displayLabel: t.tenant?.tenentType ? `${t.tenantName}(${t.tenant.tenentType})` : t.tenantName
        }));
    });
    selectedTenantId = signal<string>('');
    showTenantSelector = computed(() => {
        const clientId = this.selectedClientFilter();
        return clientId && clientId !== 'default' && this.clientTenants().length > 0;
    });


    clientPackageMappings = signal<any[]>([]);
    incompleteAuthProfileIds = signal<Set<string>>(new Set());

    private scenariosSub?: Subscription;
    private packagesSub?: Subscription;
    private functionalAreasSub?: Subscription;
    
    expiredPackages = computed(() => {
        const mappings = this.clientPackageMappings();
        if (!mappings || mappings.length === 0) return [];
        
        const now = moment().startOf('day');
        
        return mappings
            .filter((mapping: any) => {
                if (!mapping.expirationDate) return false;
                const expDate = moment(mapping.expirationDate).endOf('day');
                return expDate.isBefore(now);
            })
            .map((mapping: any) => ({
                id: mapping.id,
                packageName: mapping.package?.packageName ,
                expirationDate: new Date(mapping.expirationDate)
            }));
    });
    
    isPackageExpired = computed(() => {
        return this.expiredPackages().length > 0;
    });
    isScenarioPackageExpired(scenario: Scenario): boolean {
        if (!scenario) return false;
        const mappingId = scenario.functionalArea?.clientPackageMappingId || scenario.functionalArea?.clientPackageMapping?.id;
        if (!mappingId) return false;

        const mappings = this.clientPackageMappings();
        const mapping = mappings.find((m: any) => m.id === mappingId);

        if (!mapping || !mapping.expirationDate) return false;

        const now = moment().startOf('day');
        const expDate = moment(mapping.expirationDate).endOf('day');

        return expDate.isBefore(now);
    }
    isEditMode: boolean = false;
    isViewMode: boolean = false;
    type_test: any[] = [];
    private reportStatusPassedId: string | null = null;
    private reportStatusFailedId: string | null = null;
    private reportStatusStoppedId: string | null = null;
    function_area = signal<any[]>([]);
    // Per-row execution status for progress badges
    executionStatus = signal<Record<string, { running: boolean; current: number; total: number; passed?: number; failed?: number }>>({});
    // Track scenarios requested to stop to avoid re-setting status
    stoppingFlags = signal<Record<string, boolean>>({});
    // Multi-execution dialog state
    multiExecutionDialog = signal<boolean>(false);
    multiExecutionQueue = signal<Scenario[]>([]);
    isMultiExecuting = signal<boolean>(false);
    currentMultiExecutionIndex = signal<number>(-1);
    interScenarioDelay = signal<number>(2000); // ms between scenarios
    // Cloud execution — always enabled (local mode removed)
    executeOnCloud = () => true;
    // Cloud polling state
    private cloudPollingInterval: any = null;
    private cloudPollingStartTime: number = 0;
    private cloudExpectedSteps = signal<Record<string, number>>({});
    // Cloud job polling via localStorage of jobIds
    private static readonly PENDING_JOB_IDS_KEY = 'ta_pending_cloud_job_ids';
    currentJob = signal<{ jobId: string; status: string; createdOn?: string; scenarioCount?: number } | null>(null);
    private pendingCloudJobIdPromises = new Map<string, Promise<string | null>>();
    private pendingMultiBatchJobId: string | null = null;
    private pendingMultiBatchJobIdPromise: Promise<string | null> | null = null;
    private jobPollingTimer: any = null;
    // Execution result dialog state
    resultDialog = signal<boolean>(false);
    resultDialogData = signal<any>(null);
    // Computed grouped scenarios to avoid repeated calculations
    groupedScenarios = computed(() => {
        const queue = this.multiExecutionQueue();
        if (!queue || queue.length === 0) {
            return [];
        }
        
        const grouped = new Map<string, any[]>();
        
        try {
            queue.forEach((scenario, index) => {
                if (!scenario) return;
                // Check both paths: clientPackageMapping.package (client-specific) and package (template)
                const faName = scenario.functionalArea?.clientPackageMapping?.package?.packageName 
                    || scenario.functionalArea?.package?.packageName 
                    || 'Uncategorized';
                if (!grouped.has(faName)) {
                    grouped.set(faName, []);
                }
                const scenarioWithIndex = { ...scenario, _queueIndex: index };
                grouped.get(faName)!.push(scenarioWithIndex);
            });
            
            return Array.from(grouped.entries()).map(([functionalArea, scenarios]) => ({
                functionalArea,
                scenarios
            }));
        } catch (error) {
            return [];
        }
    });
    // Drag and drop state
    draggedScenarioIndex: number = -1;
    dropTargetIndex: number = -1;

    // === GROUP EXECUTE VIEW ===
    viewMode = signal<'list' | 'group'>('list');
    groupByField = signal<'functionalArea' | 'testType'>('functionalArea');
    groupToggleState = signal<Record<string, boolean>>({});
    allClientScenarios = signal<any[]>([]);
    allClientScenariosLoading = signal<boolean>(false);
    private allClientScenariosClientId: string = '';
    private allClientScenariosFilterHash: string = '';

    // Group execute config toggles
    groupStopOnFailure = signal<boolean>(false);
    groupContinueNext = signal<boolean>(true);
    groupSaveResults = signal<boolean>(true);

    // Computed: groups derived from ALL client scenarios (fetched on group view open)
    groupExecuteGroups = computed(() => {
        const allClientData = this.allClientScenarios();
        const field = this.groupByField();
        this.groupToggleState();

        const allScenarios = allClientData.length > 0 ? allClientData : this.scenarios();

        const grouped = new Map<string, Scenario[]>();

        allScenarios.forEach(scenario => {
            let key: string;
            if (field === 'functionalArea') {
                key = scenario.functionalArea?.name || scenario.functionalArea?.referenceValue || 'Uncategorized';
            } else {
                key = scenario.testType?.referenceValue || 'Uncategorized';
            }
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key)!.push(scenario);
        });

        return Array.from(grouped.entries()).map(([name, scenarios]) => {
            const hasSelectedScenario = scenarios.some(s => s.selected === true);
            return {
                name,
                scenarios,
                scenarioCount: scenarios.length,
                stepCount: 0,
                enabled: hasSelectedScenario,
                passRate: null as number | null
            };
        });
    });

    enabledGroups = computed(() => this.groupExecuteGroups().filter(g => g.enabled));
    disabledGroups = computed(() => this.groupExecuteGroups().filter(g => !g.enabled));

    totalEnabledScenarios = computed(() => {
        return this.enabledGroups().reduce((sum, g) => sum + g.scenarios.filter(s => s.selected === true).length, 0);
    });

    form!: FormGroup;

    constructor(
        private http: HttpClient,
        private toastService: ToastService,
        private confirmationService: ConfirmationService,
        private executionService: ExecutionService,
        private stepsService: StepsService,
        private executionResultService: ExecutionResultService,
        private testExecutorService: TestExecutorService,
        private stepPlaceholderService: StepPlaceholderService,
        private sessionPoolService: SessionPoolService,
        private functionalAreaService: FuntionalAreaService,
        private router: Router,
        private route: ActivatedRoute,
        private authService: AuthService,
        private reportApiService: ReportApiService
    ) {
        this.fb = new FormBuilder();
        this.createScenarioForm();
        // Initialize isSystemUser from AuthService
        const userInfo = this.authService.getUserInfoFromToken();
        this.isSystemUser = userInfo?.isSystemUser || false;
    }

    public setExecuteOnCloud(_enabled: boolean): void {
        // Cloud mode is always enabled — toggle removed
    }

    private startJobPollingFromLocalStorage(): void {
        if (this.jobPollingTimer) clearInterval(this.jobPollingTimer);
        // Initial load and then poll every 5 seconds
        this.pollFirstPendingJob();
        this.jobPollingTimer = setInterval(() => this.pollFirstPendingJob(), 5000);
    }

    private getPendingJobIds(): string[] {
        try {
            const raw = localStorage.getItem(ExecutionsViewModel.PENDING_JOB_IDS_KEY);
            const arr = raw ? JSON.parse(raw) : [];
            return Array.isArray(arr) ? arr.filter((x: any) => typeof x === 'string' && x) : [];
        } catch {
            return [];
        }
    }

    private setPendingJobIds(ids: string[]): void {
        try {
            localStorage.setItem(ExecutionsViewModel.PENDING_JOB_IDS_KEY, JSON.stringify(ids || []));
        } catch {}
    }

    private addPendingJobId(jobId: string): void {
        if (!jobId) return;
        const ids = this.getPendingJobIds();
        if (!ids.includes(jobId)) {
            ids.push(jobId);
            this.setPendingJobIds(ids);
        }
    }

    private removePendingJobId(jobId: string): void {
        const ids = this.getPendingJobIds();
        const next = ids.filter(id => id !== jobId);
        this.setPendingJobIds(next);
    }

    private pollFirstPendingJob(): void {
        try {
            const ids = this.getPendingJobIds();
            if (!ids || ids.length === 0) {
                this.currentJob.set(null);
                return;
            }
            const jobId = ids[0];
            this.executionService.getJobById(jobId).subscribe({
                next: (resp: any) => {
                    const job = resp?.result || resp;
                    const normalized = {
                        jobId: job?.jobId || jobId,
                        status: String(job?.status || 'queued'),
                        createdOn: (() => {
                            const raw = job?.createdOn || job?.createdAt;
                            if (!raw) return undefined;
                            const str = typeof raw === 'string' ? raw : new Date(raw).toISOString();
                            // Ensure UTC interpretation: append Z if missing timezone info
                            return str.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(str) ? str : str + 'Z';
                        })(),
                        scenarioCount: job?.scenarioCount
                    };
                    this.currentJob.set(normalized);
                    const s = normalized.status.toLowerCase();
                    if (s === 'completed' || s === 'failed' || s === 'error') {
                        this.removePendingJobId(jobId);
                    }
                },
                error: (err) => {
                    // Remove jobId only when the API indicates the job does not exist
                    const msg = String((err?.error?.message ?? err?.message ?? err?.error ?? '') || '').toLowerCase();
                    const isNotFound = (err && (err.status === 404))
                        || msg.includes('not found')
                        || msg.includes('does not exist')
                        || msg.includes('not exist');

                    if (isNotFound) {
                        this.removePendingJobId(jobId);
                        const cur = this.currentJob();
                        if (cur && cur.jobId === jobId) {
                            this.currentJob.set(null);
                        }
                    }
                    // For transient errors, leave currentJob and pending IDs untouched
                }
            });
        } catch {}
    }

    public getJobTagSeverity(status?: string): 'info' | 'success' | 'warning' | 'danger' {
        const s = (status || '').toLowerCase();
        if (s === 'running' || s === 'in-progress' || s === 'started') return 'info';
        if (s === 'queued' || s === 'pending') return 'warning';
        if (s === 'completed' || s === 'success' || s === 'done') return 'success';
        if (s === 'failed' || s === 'error') return 'danger';
        return 'info';
    }

    private createScenarioForm(): void {
        this.form = this.fb.group({
            testTypeId: [null, [Validators.required]],
            functionalAreaId: [null, [Validators.required]],
            scenarioId: [null, [Validators.required]],
            scenarioName: [null, [Validators.required, Validators.maxLength(255)]],
            scenarioDescription: [null],
        });
    }

    public init(): void {
        this.loadClients();
        this.loadReferenceData();
        this.loadIncompleteAuthProfiles();
        const hasJobs = this.getPendingJobIds().length > 0;
        if (hasJobs) this.startJobPollingFromLocalStorage();
    }

    private loadIncompleteAuthProfiles(): void {
        this.executionService.getIncompleteAuthProfileIds().subscribe({
            next: (profileIds: string[]) => {
                this.incompleteAuthProfileIds.set(new Set(profileIds));
            },
            error: () => {
                this.incompleteAuthProfileIds.set(new Set());
            }
        });
    }

    isScenarioAuthIncomplete(scenario: any): boolean {
        if (!scenario) return false;
        const incompleteIds = this.incompleteAuthProfileIds();
        if (incompleteIds.size === 0) return false;

        // Resolve all auth profile IDs linked to this scenario
        const profileIds: string[] = [];

        if (scenario.authProfileId) {
            profileIds.push(scenario.authProfileId);
        }

        if (scenario.authProfileInfo) {
            try {
                const info = typeof scenario.authProfileInfo === 'string'
                    ? JSON.parse(scenario.authProfileInfo)
                    : scenario.authProfileInfo;
                if (info?.authProfileIds?.length > 0) {
                    profileIds.push(...info.authProfileIds);
                }
            } catch { /* ignore */ }
        }

        if (profileIds.length === 0) return false;
        return profileIds.some(id => incompleteIds.has(id));
    }

    hasAnyIncompleteAuthScenarios(): boolean {
        const scenarios = this.scenarios();
        if (!scenarios || scenarios.length === 0) return false;
        return scenarios.some(s => this.isScenarioAuthIncomplete(s));
    }

    private loadClients(): void {
        this.executionService.getAllClients().subscribe({
            next: (clients: any) => {
                const clientOptions = clients.data
                    .map((client: any) => ({
                        label: client.name,
                        value: client.id
                    }));
                
                // Add "Default" option for system users at the beginning
                if (this.isSystemUser) {
                    this.clientFilterOptions.set([
                        { label: 'Master Client', value: 'default' },
                        ...clientOptions
                    ]);
                    this.isClientDropdownDisabled.set(false);
                    // Auto-select "Default" client for system users
                    this.selectedClientFilter.set('default');
                    // Trigger cascade loading for default client
                    this.onClientFilterChange('default');
                } else {
                    this.clientFilterOptions.set([...clientOptions]);
                    
                    // Auto-select and disable ONLY for single-client non-system users
                    if (clientOptions.length === 1) {
                        const singleClientId = clientOptions[0].value;
                        this.selectedClientFilter.set(singleClientId);
                        this.isClientDropdownDisabled.set(true);
                        // Trigger cascade loading for single client
                        this.onClientFilterChange(singleClientId);
                    } else {
                        this.isClientDropdownDisabled.set(false);
                    }
                }
            },
            error: (error: any) => {
                this.toastService.error('Failed to load clients');
            }
        });
    }

    public onClientFilterChange(clientId: string): void {
        // Cancel any in-flight requests from the previous client selection
        this.scenariosSub?.unsubscribe();
        this.packagesSub?.unsubscribe();
        this.functionalAreasSub?.unsubscribe();

        if (!clientId) {
            // When client is cleared, reset packages and functional areas to empty
            this.allPackages.set([]);
            this.function_area.set([]);
            this.allScenarios.set([]);
            this.clientPackageMappings.set([]);
            this.clientTenants.set([]);
            this.selectedTenantId.set('');
            this.applyAllFilters();
            return;
        }

        // Load tenants for real clients, clear for default
        if (clientId !== 'default') {
            this.loadClientTenants(clientId);
        } else {
            this.clientTenants.set([]);
            this.selectedTenantId.set('');
        }

        if (clientId === 'default') {
            this.packagesSub = this.executionService.getAllPackages().subscribe({
                next: (packages: any) => {
                    const activePkgs = packages.filter((pkg: any) => pkg && pkg.isActive);
                    this.allPackages.set(activePkgs);
                    this.clientPackageMappings.set([]);
                },
                error: (error: any) => {
                    this.toastService.error('Failed to load packages');
                }
            });
        } else {
            this.packagesSub = this.executionService.getClientPackages(clientId).subscribe({
                next: (response: any) => {
                    const mappings = Array.isArray(response) ? response : [];
                    this.clientPackageMappings.set(mappings);
                    const packages = mappings
                        .map((item: any) => item.package || item)
                        .filter((pkg: any) => pkg && pkg.isActive);
                    this.allPackages.set(packages);
                },
                error: (error: any) => {
                    this.toastService.error('Failed to load client packages');
                }
            });
        }
        
      
        if (clientId === 'default') {
            this.loadDefaultFunctionalAreas();
        } else {
            this.loadClientFunctionalAreas(clientId);
        }
        this.loadScenarios(clientId);
    }

    private loadDefaultFunctionalAreas(): void {
        this.functionalAreasSub = this.executionService.getAllFunctionalAreas().subscribe({
            next: (functionalAreas: any) => {
                // Filter to show only functional areas with null clientPackageMappingId
                const defaultAreas = functionalAreas.filter((area: any) => 
                    area.isActive && area.clientPackageMappingId === null
                );
                this.function_area.set(defaultAreas);
            },
            error: (error: any) => {
                this.toastService.error('Failed to load functional areas');
            }
        });
    }

    private loadClientFunctionalAreas(clientId: string): void {
        this.functionalAreasSub = this.executionService.getUserFunctionalAreas().subscribe({
            next: (functionalAreas: any) => {
                const areas = functionalAreas.data || functionalAreas || [];
                // Filter to show only functional areas with clientPackageMapping matching the client
                const clientAreas = areas.filter((area: any) => 
                    area.isActive && 
                    area.clientPackageMappingId !== null &&
                    area.clientPackageMapping?.clientId === clientId
                );
                this.function_area.set(clientAreas);
            },
            error: (error: any) => {
                this.toastService.error('Failed to load functional areas');
            }
        });
    }

    private loadClientTenants(clientId: string): void {
        this.executionService.getClientTenants(clientId).subscribe({
            next: (tenants: any[]) => {
                this.clientTenants.set(tenants || []);
                this.selectedTenantId.set('');
            },
            error: () => {
                this.clientTenants.set([]);
                this.selectedTenantId.set('');
            }
        });
    }

    public selectTenant(tenantId: string): void {
        this.selectedTenantId.set(tenantId);
    }


    public getSelectedTenantUrl(): string | null {
        const tenantId = this.selectedTenantId();
        if (!tenantId) return null;
        const tenant = this.clientTenants().find(t => t.id === tenantId);
        return tenant?.tenantUrl || null;
    }

    public getSelectedTenantName(): string {
        const tenantId = this.selectedTenantId();
        if (!tenantId) return '';
        const tenant = this.clientTenants().find(t => t.id === tenantId);
        return tenant?.tenantName || '';
    }

    private resolveExecutionUrl(scenarioUrl: string | undefined): string {
        const tenantUrl = this.getSelectedTenantUrl();
        if (tenantUrl) return tenantUrl;
        const clientId = this.selectedClientFilter();
        if (clientId && clientId !== 'default' && scenarioUrl) return scenarioUrl;
        return scenarioUrl || 'about:blank';
    }

    private updateTestSuiteFilterOptions(): void {
        let packages = this.allPackages();
        
        // Filter packages by selected client and package name
        const clientId = this.selectedClientFilter();
        if (clientId) {
            packages = packages.filter((pkg: any) => pkg.clientId === clientId);
        }
        
        const packageName = this.selectedPackageFilter();
        if (packageName) {
            packages = packages.filter((pkg: any) => pkg.packageName === packageName);
        }
        
        const testSuiteOptions = packages.map((pkg: any) => ({
            label: pkg.packageName,
            value: pkg.id
        }));
        
        this.testSuiteFilterOptions.set([...testSuiteOptions]);
    }

    private loadReferenceData(): void {
        this.executionService.getReferenceList(['TEST_TYPE']).subscribe((dataResult: any) => {
            this.type_test = dataResult;
        });

        // Load TEACC (Test Execution Status) reference values for report status/executionStatus
        this.executionService.getReferenceList(['TEACC']).subscribe((dataResult: any) => {
            if (Array.isArray(dataResult)) {
                const passed = dataResult.find((r: any) => r.referenceValue?.toLowerCase() === 'passed');
                const failed = dataResult.find((r: any) => r.referenceValue?.toLowerCase() === 'failed');
                const stopped = dataResult.find((r: any) => r.referenceValue?.toLowerCase() === 'stopped');
                this.reportStatusPassedId = passed?.id || null;
                this.reportStatusFailedId = failed?.id || null;
                this.reportStatusStoppedId = stopped?.id || null;
            }
        });
    }
    private loadInitialPackages(): void {
        this.executionService.getUserPackages().subscribe({
            next: (packages: any) => {
                const activePkgs = packages.filter((pkg: any) => pkg && pkg.isActive);
                this.allPackages.set(activePkgs);
            },
            error: (error: any) => {
                this.toastService.error('Failed to load packages');
            }
        });
    }


    private loadInitialFunctionalAreas(): void {
        if (this.isSystemUser) {
            this.executionService.getAllFunctionalAreas().subscribe({
                next: (functionalAreas: any) => {
                    const templateAreas = functionalAreas.filter((area: any) => 
                        area.isActive && area.clientPackageMappingId === null
                    );
                    this.function_area.set(templateAreas);
                },
                error: (error: any) => {
                    this.toastService.error('Failed to load functional areas');
                }
            });
        } else {
            this.executionService.getUserFunctionalAreas().subscribe({
                next: (response: any) => {
                    const functionalAreas = response.data || response || [];
                    const accessibleAreas = functionalAreas.filter((area: any) => area.isActive);
                    this.function_area.set(accessibleAreas);
                },
                error: (error: any) => {
                    this.toastService.error('Failed to load functional areas');
                }
            });
        }
    }

   
    private loadScenarios(clientId: string): void {
        const filterPayload = this.buildScenarioFilterPayload(clientId);
        const sortField = this.scenarioSortField();
        const sortOrder = this.scenarioSortOrder() === 1 ? 'ASC' : 'DESC';

        this.scenariosSub = this.executionService.getScenariosFiltered(
            filterPayload, this.scenarioPage(), this.scenarioPageSize(), sortField, sortOrder
        ).subscribe({
            next: (response) => {
                const scenarios = response.data || [];
                this.allScenarios.set(scenarios);
                this.scenarioTotal.set(response.total || 0);

                if (scenarios.length > 0) {
                    const scenarioMappings = scenarios
                        .map((s: any) => s.functionalArea?.clientPackageMapping)
                        .filter((m: any) => m && m.id);
                    const existingMappings = this.clientPackageMappings();
                    const allMappings = [...existingMappings, ...scenarioMappings];

                    const uniqueMappings = Array.from(
                        new Map(allMappings.map((m: any) => [m.id, m])).values()
                    );

                    this.clientPackageMappings.set(uniqueMappings);
                }

                scenarios.forEach((s: any) => {
                    s.selected = this.selectionMap.has(s.id);
                });

                this.scenarios.set(scenarios);

                // Preload all scenarios for group view (cached, won't re-fetch if filters unchanged)
                if (response.total > this.scenarioPageSize()) {
                    this.loadAllClientScenarios();
                }
            },
            error: (error) => {
                this.toastService.error('Failed to load scenarios');
            }
        });
    }

    private selectionMap = new Map<string, any>();

    scenarioSearchText = signal<string>('');
    private searchDebounceTimer: any = null;

    public onScenarioSearchInput(value: string): void {
        this.scenarioSearchText.set(value);
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
        const trimmed = (value || '').trim();
        if (trimmed.length > 0 && trimmed.length < 3) return;
        this.searchDebounceTimer = setTimeout(() => {
            this.scenarioPage.set(1);
            this.scenarioFirst.set(0);
            const clientId = this.selectedClientFilter();
            if (clientId) this.loadScenarios(clientId);
        }, 700);
    }

    private buildScenarioFilterPayload(clientId: string): any {
        const clientIdValue = clientId === 'default' ? 'default' : clientId;
        let packageId = this.selectedTestSuiteFilter() || '';
        if (!packageId && this.selectedPackageFilter()) {
            packageId = this.allPackages().find(p => p.packageName === this.selectedPackageFilter())?.id || '';
        }
        return {
            filter: [
                { name: 'clientId', value: clientIdValue },
                { name: 'packageId', value: packageId },
                { name: 'functionalAreaId', value: this.selectedFunctionalAreaFilter() || '' },
                { name: 'testTypeId', value: this.filterTestType() },
                { name: 'search', value: this.scenarioSearchText() || '' },
            ]
        };
    }

    public onScenarioSearch(search: string): void {
        this.scenarioSearchText.set(search);
        this.scenarioPage.set(1);
        this.scenarioFirst.set(0);
        const clientId = this.selectedClientFilter();
        if (clientId) this.loadScenarios(clientId);
    }

    public clearScenarioSearch(): void {
        this.scenarioSearchText.set('');
        this.scenarioPage.set(1);
        this.scenarioFirst.set(0);
        const clientId = this.selectedClientFilter();
        if (clientId) this.loadScenarios(clientId);
    }

    public onScenarioPageChange(event: any): void {
        const newPageSize = event.rows || 10;
        const pageSizeChanged = newPageSize !== this.scenarioPageSize();
        if (pageSizeChanged) {
            this.scenarioPageSize.set(newPageSize);
            this.scenarioPage.set(1);
            this.scenarioFirst.set(0);
        } else {
            const page = Math.floor((event.first || 0) / newPageSize) + 1;
            this.scenarioPage.set(page);
            this.scenarioFirst.set(event.first || 0);
        }

        if (event.sortField) {
            this.scenarioSortField.set(event.sortField);
            this.scenarioSortOrder.set(event.sortOrder ?? 1);
        }

        const clientId = this.selectedClientFilter();
        if (clientId) this.loadScenarios(clientId);
    }

    public viewScenario(scenarioData: Scenario): void {
        this.selectedScenario = scenarioData;
        this.isEditMode = false;
        this.isViewMode = true;
        this.scenarioDialog = true;

        this.form.patchValue({
            scenarioName: scenarioData.scenarioName,
            scenarioId: scenarioData.scenarioId,
            testTypeId: scenarioData.testTypeId,
            functionalAreaId: scenarioData.functionalAreaId,
            scenarioDescription: scenarioData.scenarioDescription,
        });

        this.form.disable();
    }

    public editScenario(scenarioData: Scenario): void {
        // this.selectedScenario = scenarioData;
        // this.isEditMode = true;
        // this.isViewMode = false;
        // this.scenarioDialog = true;

        // this.form.patchValue({
        //   scenarioName: scenarioData.scenarioName,
        //   scenarioDescription: scenarioData.scenarioDescription,
        //   scenarioId: scenarioData.scenarioId,
        //   testTypeId: scenarioData.testTypeId,
        //   functionalAreaId: scenarioData.functionalAreaId,
        // });
    }

    public deleteScenario(scenarioData: Scenario): void {
        //   this.confirmationService.confirm({
        //     message: `Are you sure you want to delete the scenario "${scenarioData.scenarioName}"?`,
        //     header: 'Confirm',
        //     acceptLabel: 'Yes',
        //     rejectLabel: 'No',
        //     acceptButtonStyleClass: 'p-button-primary',
        //     rejectButtonStyleClass: 'p-button-warn',
        //     defaultFocus: 'reject',
        //     accept: () => {
        //       this.executionService.deleteScenario(scenarioData.id).subscribe({
        //         next: () => {
        //           this.toastService.success('Scenario deleted successfully');
        //           this.loadScenarios();
        //         },
        //         error: (error) => {
        //           this.toastService.error(error.error?.message || 'Failed to delete scenario');
        //         }
        //       });
        //     }
        //   });
    }

    public inspectScenario(scenario: Scenario): void {
        if (!scenario?.id) return;
        this.router.navigate(['/scenario/capture', scenario.functionalAreaId, scenario.id]);
    }

    public refreshScenario(scenario: Scenario): void {
        if (!scenario?.id) return;
        const clientId = this.selectedClientFilter();
        if (clientId) {
            this.loadScenarios(clientId);
        }
    }

    public runScenario(scenario: Scenario): void {
        const sid = scenario.id;
        if (!sid) {
            this.toastService.error('Invalid scenario id');
            return;
        }
        if (this.showTenantSelector() && !this.selectedTenantId()) {
            this.toastService.error('Please select a tenant before executing.');
            return;
        }
        if (this.isScenarioAuthIncomplete(scenario)) {
            this.toastService.error('Cannot execute: Authentication profile credentials are incomplete. Please update the credentials in Auth Profiles.');
            return;
        }
        if (this.isScenarioPackageExpired(scenario)) {
            return;
        }

        // Clear any previous status so the row starts fresh
        this.clearExecutionStatus(sid);
        // Ensure stop flag is cleared for a fresh run
        this.clearStoppingFlag(sid);
        // Immediately mark as running so the UI reflects execution state
        this.setExecutionStatus(sid, { running: true, current: 0, total: 0 });

        const isCloud = this.executeOnCloud();
        this.toastService.info(isCloud ? 'Enqueuing cloud execution' : 'Starting execution');

        // Cloud path: enqueue to SQS — ECS runner handles execution and calls PUT /runner/jobs/:jobId/status
        if (isCloud) {
            this.executionService.enqueueCloudScenarioRun(sid, this.selectedTenantId()).subscribe({
                next: (res) => {
                    const jobId = (res as any)?.result?.jobId || (res as any)?.jobId || '';
                    if (jobId) {
                        this.addPendingJobId(jobId);
                        if (!this.jobPollingTimer) this.startJobPollingFromLocalStorage();
                    }
                    this.toastService.success('Cloud run enqueued');
                },
                error: (err) => {
                    this.toastService.error(err?.error?.message || 'Failed to enqueue cloud run');
                }
            });
            return;
        }

  
        this.pendingCloudJobIdPromises.set(sid,
            new Promise<string | null>(resolve => {
                this.executionService.enqueueCloudScenarioRun(sid, this.selectedTenantId()).subscribe({
                    next: (res) => {
                        const jobId = (res as any)?.result?.jobId || (res as any)?.jobId || null;
                     
                        resolve(jobId);
                    },
                    error: (err) => {
                      
                        resolve(null);
                    }
                });
            })
        );

        // 1) Load scenario detail to get initial URL and packageId
        this.executionService.getScenarioById(sid).subscribe({
            next: async (sc: any) => {
                const initialUrl = this.resolveExecutionUrl(sc?.url);
                const functionalAreaId = sc?.functionalAreaId || '';
                const clientPackageMappingId = sc?.functionalArea?.clientPackageMapping?.id || '';

                // Await the jobId so we can use it as executionRunId for result correlation
                const jobIdPromise = this.pendingCloudJobIdPromises.get(sid);
                const resolvedJobId = jobIdPromise ? await jobIdPromise : null;
                const executionRunId = resolvedJobId || uuidv4();

                // Set context with all parameters including tenant
                this.executionResultService.setExecutionContext(functionalAreaId, sid, clientPackageMappingId, this.selectedTenantId() || undefined);
                this.executionResultService.startSession(functionalAreaId, sid, clientPackageMappingId, this.selectedTenantId() || undefined);

                // NEW: Check if preconditions are required
                const resolvedAuthProfileId = this.resolveAuthProfileId(sc);
                const hasPreconditions = resolvedAuthProfileId || sc?.screenId;
                const useSessionWindow = hasPreconditions;

                // 2) Create hidden host OR session window based on preconditions
                const bridge: any = (window as any).windowHost;
                const hostBridge = (window as any).host;

                if (!bridge || (!bridge.createHiddenHost && !hostBridge?.createSessionWindow)) {
                    this.toastService.error('Browser host API not available');
                    return;
                }

                let sessionHandle: number | null = null;
                let sessionState: any = null;

                if (useSessionWindow) {
                    // Use session pool for proper session management and reuse
                    try {
                        sessionState = await this.sessionPoolService.acquireSession(
                            {
                                authProfileId: resolvedAuthProfileId!,
                                tenantId: sc.tenantId
                            },
                            'REUSE'  // Reuse existing session if available
                        );
                        sessionHandle = sessionState.windowHandle;
                    } catch (sessionError: any) {
                        this.toastService.error(`Session acquisition failed: ${sessionError.message}`);
                        return;
                    }

                    // Execute preconditions (login + screen navigation)
                    try {
                        this.toastService.info('Setting up preconditions (login + navigation)...');
                        
                        // Initialize session pool with scenario's screen master
                        const screens = sc.screenMaster ? [sc.screenMaster] : [];
                        // Ensure screen has screenId field matching scenario.screenId
                        if (screens.length > 0 && sc.screenId) {
                          screens[0].screenId = sc.screenId;
                        }
                        await this.sessionPoolService.initialize([], screens);
                        
                        const preconditionResult = await this.sessionPoolService.executePreconditions(
                            sc,
                            sessionState,
                            [] // Empty modules array - will use generated modules from auth profile
                        );

                        if (!preconditionResult.success) {
                            this.toastService.error(`Precondition failed: ${preconditionResult.error}`);
                            if (sessionHandle) {
                                await hostBridge.destroySessionWindow(sessionHandle);
                            }
                            return;
                        }

                        this.toastService.success('Preconditions completed successfully');
                        
                        //  Log precondition execution to database
                        this.executionResultService.logPreconditionExecution(preconditionResult);
                        
                        // Set current session for test executor
                        this.testExecutorService.setSession(sessionState);

                    } catch (preconditionError: any) {
                        this.toastService.error(`Precondition failed: ${preconditionError?.message || preconditionError}`);
                        if (sessionHandle) {
                            await hostBridge.destroySessionWindow(sessionHandle);
                        }
                        return;
                    }
                } else {
                    // Use legacy hidden host for scenarios without preconditions
                    const res: any = await bridge.createHiddenHost({ initialUrl });
                    if (!res?.success) {
                        this.toastService.error(res?.error || 'Failed to create hidden host');
                        return;
                    }

                    // 2a) Clear browser session AFTER creating hidden host (ensures clean state for login)
                    try {
                        if (hostBridge && hostBridge.clearSession) {
                            await hostBridge.clearSession();
                        }
                    } catch (clearError) {
                        // Continue execution even if clear fails
                    }

                    // CRITICAL: Navigate to initial URL before executing steps
                    if (initialUrl && initialUrl !== 'about:blank') {
                        try {
                            await new Promise(resolve => setTimeout(resolve, 2000)); // Wait for browser to be ready
                            
                            if (!hostBridge || !hostBridge.navigate) {
                                this.toastService.error('Browser navigation API not available');
                                return;
                            }
                            
                            await hostBridge.navigate(initialUrl);
                            await new Promise(resolve => setTimeout(resolve, 3000)); // Wait for page to load
                        } catch (navError) {
                            this.toastService.error(`Navigation failed: ${navError}`);
                            return;
                        }
                    }
                }

                // 3) Fetch steps and build step input string (resolved placeholders)
                this.stepsService.getStepsByScenario(sid).subscribe({
                    next: async (res: any) => {
                        // Handle both raw array and { isSuccess, data } API responses
                        const stepsArray: any[] = Array.isArray(res)
                            ? res
                            : (res && Array.isArray(res.data))
                                ? res.data
                                : [];

                        const active = stepsArray.filter(s => s.isActive !== false).sort((a, b) => (a.stepId || 0) - (b.stepId || 0));

                        if (active.length === 0) {
                            this.toastService.error('No active steps found for this scenario');
                            // Cleanup session/host
                            try {
                                if (useSessionWindow && sessionHandle) {
                                    await hostBridge.destroySessionWindow(sessionHandle);
                                } else {
                                    await bridge.destroyHiddenHost();
                                }
                            } catch {}
                            return;
                        }

                        // Initialize row progress
                        if (!this.isStopping(sid)) {
                            this.setExecutionStatus(sid, { running: true, current: 0, total: active.length });
                        }
                        const stepInput = active.map(s => {
                            const addInfo = this.stepPlaceholderService.parseAdditionalInfo(s.additionalInfo || '{}');
                            const resolved = s.placeholder && s.placeholder.trim()
                                ? this.stepPlaceholderService.resolvePlaceholder(s.placeholder, addInfo)
                                : s.stepDescription;
                            return resolved;
                        }).join('\n');

                        const stepMetadata = active.map(s => {
                            const addInfo = this.stepPlaceholderService.parseAdditionalInfo(s.additionalInfo || '{}');
                            return {
                                stepId: s.stepId,
                                stepUuid: s.id,
                                stepDescription: (s.placeholder && s.placeholder.trim())
                                  ? this.stepPlaceholderService.resolvePlaceholder(s.placeholder, addInfo)
                                  : s.stepDescription,
                                isRecording: s.isRecording || false,
                                additionalInfo: addInfo  // Include parsed additional info for recorded steps
                            };
                        });

                        let executionSucceeded = false;
                        try {
                            const report = await this.testExecutorService.executeScenario(stepInput, {
                                stopOnFailure: true,
                                stepMetadata,
                                executionContext: { functionalAreaId, scenarioId: sid, clientPackageMappingId, executionRunId, tenantId: this.selectedTenantId() || undefined },
                                onProgress: (current, total) => {
                                    if (!this.isStopping(sid)) {
                                        this.setExecutionStatus(sid, { running: true, current, total });
                                    }
                                }
                            });
                            // Finish row progress unless it was stopped
                            if (!this.isStopping(sid)) {
                                this.setExecutionStatus(sid, { running: false, current: report.totalSteps, total: report.totalSteps, passed: report.passed, failed: report.failed });
                                this.toastService.success(`Execution complete. Passed: ${report.passed}, Failed: ${report.failed}`);
                            }
                            // Save report record (flag as stopped if user requested stop)
                            this.saveReportAfterExecution(sc, sid, report, undefined, this.isStopping(sid));
                            executionSucceeded = true;
                        } catch (err) {
                            if (!this.isStopping(sid)) {
                                this.setExecutionStatus(sid, { running: false, current: 0, total: 0, failed: 1 });
                                this.toastService.error('Execution failed');
                            }
                        } finally {
                            // Update job status if this was a sandbox-registered job
                            const jobIdPromise = this.pendingCloudJobIdPromises.get(sid);
                       
                            if (jobIdPromise) {
                                this.pendingCloudJobIdPromises.delete(sid);
                                const cloudJobId = await jobIdPromise;
                              
                                if (cloudJobId) {
                                    try {
                                        const r = await firstValueFrom(this.executionService.updateJobStatus(cloudJobId, executionSucceeded ? 'completed' : 'failed'));
                                      
                                    } catch (e) {
                                       
                                    }
                                }
                            }
                            // CRITICAL: Wait for all pending saves to complete before destroying host
                            try { await this.executionResultService.waitForPendingSaves(); } catch {}
                            
                            // Cleanup: destroy session window or hidden host
                            try {
                                if (useSessionWindow && sessionHandle) {
                                    const hostBridge = (window as any).host;
                                    if (hostBridge?.destroySessionWindow) {
                                        await hostBridge.destroySessionWindow(sessionHandle);
                                    }
                                    // Clear current session from test executor
                                    this.testExecutorService.setSession(null);
                                } else {
                                    await bridge.destroyHiddenHost();
                                }
                            } catch (destroyError) {
                                // Silently handle cleanup errors
                            }
                            
                            try { this.executionResultService.endSession(); } catch {}
                            
                            // If stopped, retain status (stopped X/Y) and clear stop flag
                            if (this.isStopping(sid)) {
                                const map = { ...this.executionStatus() };
                                const s = map[sid];
                                if (s) {
                                    map[sid] = { ...s, running: false };
                                    this.executionStatus.set(map);
                                }
                                this.clearStoppingFlag(sid);
                            }
                        }
                    },
                    error: (_error: any) => {
                        this.toastService.error('Failed to load steps');
                        if (!this.isStopping(sid)) {
                            this.setExecutionStatus(sid, { running: false, current: 0, total: 0, failed: 1 });
                        }
                        try { bridge.destroyHiddenHost(); } catch {}
                    }
                });
            },
            error: (_err: any) => {
                this.toastService.error('Failed to load scenario');
            }
        });
    }

    private setExecutionStatus(scenarioId: string, status: { running?: boolean; current?: number; total?: number; passed?: number; failed?: number }) {
        const current = { ...this.executionStatus() };
        const prev = current[scenarioId] || { running: false, current: 0, total: 0 };
        current[scenarioId] = { ...prev, ...status } as any;
        this.executionStatus.set(current);
    }

    public isRunning(scenario: Scenario): boolean {
        const st = this.executionStatus()[scenario.id];
        return !!st?.running;
    }

    public getExecutionBadge(scenario: Scenario): string | null {
        const st = this.executionStatus()[scenario.id];
        if (st?.running && st.total > 0) {
            return `${st.current}/${st.total}`;
        }
        if (st && !st.running && st.total > 0) {
            const passed = st.passed || 0;
            const failed = st.failed || 0;
            return `${passed}/${passed + failed}`;
        }
        return null;
    }

    public getExecutionBadgeClass(scenario: Scenario): string {
        const st = this.executionStatus()[scenario.id];
        if (st?.running) return 'p-badge-info';
        if (st && !st.running && (st.failed || 0) > 0) return 'p-badge-danger';
        if (st && !st.running && (st.passed || 0) > 0) return 'p-badge-success';
        return '';
    }

    public stopScenario(scenario: Scenario): void {
        try {
            this.toastService.info('Stopping execution...');
            // Flag this scenario as stopping
            this.setStoppingFlag(scenario.id);
            this.testExecutorService.stopExecution();
            // Mark as not running and keep current/total to show Stopped X/Y
            const currentMap = { ...this.executionStatus() };
            const st = currentMap[scenario.id];
            if (st) {
                currentMap[scenario.id] = { ...st, running: false };
                this.executionStatus.set(currentMap);
            }
            // Attempt to tear down hidden host to stop further progress updates
            // NOTE: Do NOT call endSession() here — it clears context (scenarioId etc.)
            // before the failed step's result can be saved. The finally block in the
            // execution flow already calls endSession() after waitForPendingSaves().
            const bridge: any = (window as any).windowHost;
            try { bridge?.destroyHiddenHost?.(); } catch {}
        } catch (e) {
            this.toastService.error('Failed to stop execution');
        }
    }

    private autoClearStatus(scenarioId: string, timeoutMs: number = 4000) {
        setTimeout(() => {
            const current = { ...this.executionStatus() };
            if (current[scenarioId] && !current[scenarioId].running) {
                delete current[scenarioId];
                this.executionStatus.set(current);
            }
        }, timeoutMs);
    }

    public clearExecutionStatus(scenarioId: string): void {
        const current = { ...this.executionStatus() };
        if (current[scenarioId]) {
            delete current[scenarioId];
            this.executionStatus.set(current);
        }
    }

    public clearAllExecutionStatuses(): void {
        this.executionStatus.set({});
    }

    private setStoppingFlag(scenarioId: string): void {
        const flags = { ...this.stoppingFlags() };
        flags[scenarioId] = true;
        this.stoppingFlags.set(flags);
    }

    private clearStoppingFlag(scenarioId: string): void {
        const flags = { ...this.stoppingFlags() };
        if (flags[scenarioId]) {
            delete flags[scenarioId];
            this.stoppingFlags.set(flags);
        }
    }

    private isStopping(scenarioId: string): boolean {
        return !!this.stoppingFlags()[scenarioId];
    }

    public hideDialog(): void {
        this.selectedScenario = null;
        this.scenarioDialog = false;
        this.isEditMode = false;
        this.isViewMode = false;
        this.form.reset();
        this.form.enable();
    }

    public onGlobalFilter(table: Table, event: Event): void {
        table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
    }

    public filterByFunctionalArea(functionalAreaId: string): void {
        this.selectedFunctionalAreaFilter.set(functionalAreaId);
        this.scenarioPage.set(1);
        this.scenarioFirst.set(0);
        const clientId = this.selectedClientFilter();
        if (clientId) this.loadScenarios(clientId);
        if (this.viewMode() === 'group') this.loadAllClientScenarios(true);
    }

    public filterByTestType(testTypeIds: string[] | string): void {
        const testTypeArray = Array.isArray(testTypeIds) ? testTypeIds : (testTypeIds ? [testTypeIds] : []);

        this.filterTestType.set(testTypeArray);
        this.selectedTestTypeFilter.set(testTypeArray);

        this.selectedFunctionalAreaFilter.set('');

        this.scenarioPage.set(1);
        this.scenarioFirst.set(0);
        const clientId = this.selectedClientFilter();
        if (clientId) this.loadScenarios(clientId);
        if (this.viewMode() === 'group') this.loadAllClientScenarios(true);
    }

    public filterByPackageName(packageName: string): void {
        this.selectedPackageFilter.set(packageName);
        this.selectedTestSuiteFilter.set('');
        this.selectedFunctionalAreaFilter.set('');
        this.updateTestSuiteFilterOptions();
        this.scenarioPage.set(1);
        this.scenarioFirst.set(0);
        const clientId = this.selectedClientFilter();
        if (clientId) this.loadScenarios(clientId);
        if (this.viewMode() === 'group') this.loadAllClientScenarios(true);
    }

    public clearPackageFilter(): void {
        this.selectedPackageFilter.set('');
        this.selectedTestSuiteFilter.set('');
        this.selectedFunctionalAreaFilter.set('');
        this.updateTestSuiteFilterOptions();
        this.scenarioPage.set(1);
        this.scenarioFirst.set(0);
        const clientId = this.selectedClientFilter();
        if (clientId) this.loadScenarios(clientId);
        if (this.viewMode() === 'group') this.loadAllClientScenarios(true);
    }

    public filterByTestSuite(testSuiteId: string): void {
        this.selectedTestSuiteFilter.set(testSuiteId);
        this.scenarioPage.set(1);
        this.scenarioFirst.set(0);
        const clientId = this.selectedClientFilter();
        if (clientId) this.loadScenarios(clientId);
        if (this.viewMode() === 'group') this.loadAllClientScenarios(true);
    }

    public clearTestSuiteFilter(): void {
        this.selectedTestSuiteFilter.set('');
        this.scenarioPage.set(1);
        this.scenarioFirst.set(0);
        const clientId = this.selectedClientFilter();
        if (clientId) this.loadScenarios(clientId);
        if (this.viewMode() === 'group') this.loadAllClientScenarios(true);
    }

    public clearFunctionalAreaFilter(): void {
        this.selectedFunctionalAreaFilter.set('');
        this.scenarioPage.set(1);
        this.scenarioFirst.set(0);
        const clientId = this.selectedClientFilter();
        if (clientId) this.loadScenarios(clientId);
        if (this.viewMode() === 'group') this.loadAllClientScenarios(true);
    }

    public clearTestTypeFilter(): void {
        this.filterTestType.set([]);
        this.selectedTestTypeFilter.set([]);
        this.selectedFunctionalAreaFilter.set('');

        this.scenarioPage.set(1);
        this.scenarioFirst.set(0);
        const clientId = this.selectedClientFilter();
        if (clientId) this.loadScenarios(clientId);
        if (this.viewMode() === 'group') this.loadAllClientScenarios(true);
    }

    public filterByClient(clientId: string): void {
        this.selectedClientFilter.set(clientId);
        this.selectedPackageFilter.set('');
        this.selectedTestSuiteFilter.set('');
        this.selectedFunctionalAreaFilter.set('');
        this.selectedTestTypeFilter.set([]);
        this.filterTestType.set([]);
        this.scenarioSearchText.set('');
        this.groupToggleState.set({});
        this.selectionMap.clear();
        this.allClientScenarios.set([]);
        this.scenarioPage.set(1);
        this.scenarioFirst.set(0);
        this.onClientFilterChange(clientId);
        this.updateTestSuiteFilterOptions();
    }

    public clearClientFilter(): void {
        if (this.isSystemUser) {
            return;
        }

        if (this.isClientDropdownDisabled()) {
            return;
        }

        this.selectedClientFilter.set('');
        this.selectedPackageFilter.set('');
        this.selectedTestSuiteFilter.set('');
        this.selectedFunctionalAreaFilter.set('');
        this.selectedTestTypeFilter.set([]);
        this.filterTestType.set([]);
        this.scenarioSearchText.set('');
        this.allPackages.set([]);
        this.function_area.set([]);
        this.allScenarios.set([]);
        this.clientTenants.set([]);
        this.selectedTenantId.set('');
        this.selectionMap.clear();
        this.allClientScenarios.set([]);
        this.updateTestSuiteFilterOptions();
        this.applyAllFilters();
    }

    public resetClientToDefault(): void {
        if (this.isSystemUser) {
            this.selectedClientFilter.set('default');
            this.onClientFilterChange('default');
        } else {
            const options = this.clientFilterOptions();
            if (options.length === 1) {
                this.selectedClientFilter.set(options[0].value);
                this.onClientFilterChange(options[0].value);
            } else {
                this.selectedClientFilter.set('');
                this.selectedPackageFilter.set('');
                this.selectedTestSuiteFilter.set('');
                this.selectedFunctionalAreaFilter.set('');
                this.selectedTestTypeFilter.set([]);
                this.allPackages.set([]);
                this.function_area.set([]);
                this.allScenarios.set([]);
                this.clientTenants.set([]);
                this.selectedTenantId.set('');
                this.updateTestSuiteFilterOptions();
                this.applyAllFilters();
            }
        }
    }

    public getClientLabel(scenario: Scenario): string {
        const clientName = scenario?.functionalArea?.clientPackageMapping?.client?.name;
        return clientName || 'Default';
    }

    private applyAllFilters(): void {
        this.scenarios.set([...this.allScenarios()]);
    }


    public validateAll(): void {
        const scenarios = this.allScenarios();
        // Only select scenarios whose packages are NOT expired
        const updated = scenarios.map(s => ({
            ...s,
            selected: !this.isScenarioPackageExpired(s) && !this.isScenarioAuthIncomplete(s) 
        }));
        this.allScenarios.set(updated);
        this.applyAllFilters();
    }

    getSelectedScenarios(): Scenario[] {
        return Array.from(this.selectionMap.values());
    }

    getSelectedCount(): number {
        return this.selectionMap.size;
    }

    allSelected = computed(() => {
        const scenarios = this.scenarios();
        if (!scenarios || scenarios.length === 0) return false;
        const selectable = scenarios.filter((s: any) => s.stepCount && s.stepCount > 0 && !this.isScenarioPackageExpired(s) && !this.isScenarioAuthIncomplete(s));
        return selectable.length > 0 && selectable.every(s => s.selected);
    });

    isAllSelected(): boolean {
        return this.allSelected();
    }

    toggleSelectAll(checked: boolean): void {
        const scenarios = this.scenarios();
        scenarios.forEach((s: any) => {
            if ((s.stepCount && s.stepCount > 0) && !this.isScenarioPackageExpired(s) && !this.isScenarioAuthIncomplete(s)) {
                s.selected = checked;
            }
        });
        this.syncSelectionMap();
        this.scenarios.set([...scenarios]);
    }

    getFaBadgeClass(scenario: Scenario): string {
        const name = (scenario.functionalArea?.name || scenario.functionalArea?.referenceValue || '').toLowerCase();
        if (name.includes('hcm') || name.includes('human')) return 'b-hcm';
        if (name.includes('payroll') || name.includes('pay')) return 'b-pay';
        if (name.includes('benefit')) return 'b-ben';
        if (name.includes('time') || name.includes('track')) return 'b-tt';
        if (name.includes('security') || name.includes('sec')) return 'b-sec';
        if (name.includes('navigation') || name.includes('nav')) return 'b-nav';
        if (name.includes('form')) return 'b-form';
        if (name.includes('recruit') || name.includes('rec')) return 'b-rec';
        if (name.includes('talent') || name.includes('tal')) return 'b-tal';
        if (name.includes('integrat') || name.includes('int')) return 'b-int';
        if (name.includes('finance') || name.includes('fin')) return 'b-fin';
        if (name.includes('report') || name.includes('rep')) return 'b-rep';
        if (name.includes('absence') || name.includes('abs')) return 'b-abs';
        if (name.includes('comp')) return 'b-comp';
        return 'b-default';
    }

    getExpirationMessage(): string {
        const expired = this.expiredPackages();
        if (!expired || expired.length === 0) return '';
        
        if (expired.length === 1) {
            const pkg = expired[0];
            const expDateStr = moment(pkg.expirationDate).format('MMMM D, YYYY');
            return `${pkg.packageName} package expired on ${expDateStr}. Execution is not permitted.`;
        } else {
            const packageList = expired.map(pkg => {
                const expDateStr = moment(pkg.expirationDate).format('MMMM D, YYYY');
                return `${pkg.packageName} package (expired ${expDateStr}) `;
            }).join(', ');
            return `The following packages have expired: ${packageList}. Execution is not permitted.`;
        }
    }

    openMultiExecutionDialog(): void {
        const selected = this.getSelectedScenarios();
        if (selected.length === 0) {
            this.toastService.error('Please select at least one scenario');
            return;
        }
        
        this.multiExecutionQueue.set([...selected]);
        
        // Small delay to ensure queue is set before opening dialog
        setTimeout(() => {
            this.multiExecutionDialog.set(true);
        }, 50);
    }

    closeMultiExecutionDialog(): void {
        if (this.isMultiExecuting()) {
            this.confirmationService.confirm({
                message: 'Execution is in progress. Do you want to stop and close?',
                header: 'Confirm',
                acceptLabel: 'Yes, Stop',
                rejectLabel: 'No',
                accept: () => {
                    this.stopMultiExecution();
                    this.multiExecutionDialog.set(false);
                    this.multiExecutionQueue.set([]);
                    this.executionStatus.set({});
                    this.currentMultiExecutionIndex.set(-1);
                }
            });
        } else {
            this.multiExecutionDialog.set(false);
            this.multiExecutionQueue.set([]);
            this.executionStatus.set({});
            this.currentMultiExecutionIndex.set(-1);
        }
    }

    getCurrentScenarioProgress() {
        const currentIndex = this.currentMultiExecutionIndex();
        if (currentIndex < 0 || !this.isMultiExecuting()) return null;
        
        const queue = this.multiExecutionQueue();
        const scenario = queue[currentIndex];
        if (!scenario) return null;
        
        const status = this.executionStatus()[scenario.id];
        return status;
    }

    // Multi-execution is completed only when all scenarios have final pass/fail results
    isMultiExecutionCompleted(): boolean {
        const queue = this.multiExecutionQueue();
        if (!queue || queue.length === 0) return false;
        if (this.isMultiExecuting()) return false;
        const statuses = this.executionStatus();
        return queue.every(s => {
            const st = statuses[s.id];
            return !!st && !st.running && ((st.passed !== undefined) || (st.failed !== undefined));
        });
    }

    // Aggregate a concise summary for multi-scenario execution state
    // Counts only completed scenarios (where pass/fail values are defined)
    // and tracks stopped scenarios separately
    getMultiExecutionSummary(): { totalScenarios: number; passedScenarios: number; failedScenarios: number; stoppedScenarios: number } {
        const queue = this.multiExecutionQueue();
        const statuses = this.executionStatus();

        if (!queue || queue.length === 0) {
            return { totalScenarios: 0, passedScenarios: 0, failedScenarios: 0, stoppedScenarios: 0 };
        }

        let passedScenarios = 0;
        let failedScenarios = 0;
        let stoppedScenarios = 0;
        let completedScenarios = 0;

        for (const scenario of queue) {
            const st = statuses[scenario.id];
            if (!st || st.running) continue;
            const hasFinal = (st.passed !== undefined) || (st.failed !== undefined);
            if (hasFinal) {
                completedScenarios++;
                const failed = st.failed || 0;
                if (failed > 0) failedScenarios++; else passedScenarios++;
            } else {
                // Stopped or incomplete scenario (no final pass/fail counts)
                stoppedScenarios++;
            }
        }

        return {
            totalScenarios: completedScenarios,
            passedScenarios,
            failedScenarios,
            stoppedScenarios
        };
    }

    getTotalStepsProgress(): number {
        const queue = this.multiExecutionQueue();
        if (!queue || queue.length === 0) return 0;
        
        const allStatuses = this.executionStatus();
        let totalStepsCompleted = 0;
        let totalStepsOverall = 0;
        
        queue.forEach((scenario) => {
            const status = allStatuses[scenario.id];
            if (status) {
                totalStepsCompleted += status.current || 0;
                totalStepsOverall += status.total || 0;
            }
        });
        
        return totalStepsOverall > 0 ? Math.round((totalStepsCompleted / totalStepsOverall) * 100) : 0;
    }

    getScenarioResult(scenarioId: number) {
        const status = this.executionStatus()[scenarioId];
        if (!status) return null;
        
        return {
            passed: status.passed || 0,
            failed: status.failed || 0,
            total: status.total || 0,
            stepsExecuted: status.current || status.total || 0
        };
    }

    // Execute multiple scenarios directly without opening dialog
    executeMultipleScenariosDirectly(): void {
        const selected = this.getSelectedScenarios();
        if (selected.length === 0) {
            this.toastService.error('Please select at least one scenario');
            return;
        }

        if (this.showTenantSelector() && !this.selectedTenantId()) {
            this.toastService.error('Please select a tenant before executing.');
            return;
        }

        // Block if any selected scenario has incomplete auth profiles
        const incompleteScenarios = selected.filter(s => this.isScenarioAuthIncomplete(s));
        if (incompleteScenarios.length > 0) {
            const names = incompleteScenarios.map(s => s.scenarioId || s.scenarioName).join(', ');
            this.toastService.error(`Cannot execute: ${incompleteScenarios.length} scenario(s) have incomplete authentication profiles (${names}). Please update credentials in Auth Profiles.`);
            return;
        }

        const isCloud = this.executeOnCloud();
        console.log('[ExecuteDirectly] executeOnCloud =', isCloud);

        // Cloud path: enqueue to SQS — ECS runner handles execution and calls PUT /runner/jobs/:jobId/status
        if (isCloud) {
            const scenarioIds = selected.map(s => s.id).filter((id): id is string => !!id);
            if (scenarioIds.length !== selected.length) {
                this.toastService.error('Some scenarios have invalid IDs');
                return;
            }

            this.toastService.info(`Enqueuing ${scenarioIds.length} scenarios for cloud execution`);
            this.executionService.enqueueCloudScenarioRun(scenarioIds, this.selectedTenantId()).subscribe({
                next: (res) => {
                    const r: any = res;
                    const jobId: string = r?.result?.jobId || r?.jobId || '';
                    if (jobId) {
                        this.addPendingJobId(jobId);
                        if (!this.jobPollingTimer) this.startJobPollingFromLocalStorage();
                    }
                    this.toastService.success(`${scenarioIds.length} scenarios enqueued for cloud execution`);
                },
                error: (err) => {
                    this.toastService.error(err?.error?.message || 'Failed to enqueue cloud runs');
                }
            });
            return;
        }

        // Local/sandbox path: register a batch job first so we can send a completion notification
        // Store as Promise so the completion handler can await it (avoids race condition on fast tests)
        const sandboxScenarioIds = selected.map(s => s.id).filter((id): id is string => !!id);
        if (sandboxScenarioIds.length > 0) {
            this.pendingMultiBatchJobIdPromise = new Promise<string | null>(resolve => {
                this.executionService.enqueueCloudScenarioRun(sandboxScenarioIds, this.selectedTenantId()).subscribe({
                    next: (res) => {
                        const r: any = res;
                        const jobId: string = r?.result?.jobId || r?.jobId || '';
                        if (jobId) this.pendingMultiBatchJobId = jobId;
                        resolve(jobId || null);
                    },
                    error: () => resolve(null)
                });
            });
        }

        // Local path: set queue and execute
        this.multiExecutionQueue.set([...selected]);
        this.executeMultipleScenarios();
    }

    // View scenario result - open dialog with execution results
    async viewScenarioResult(scenario: Scenario): Promise<void> {
        if (!scenario.id) {
            this.toastService.error('Invalid scenario');
            return;
        }
        
        try {
            // Open dialog immediately to improve perceived performance
            this.resultDialogData.set(null);
            this.resultDialog.set(true);

            // Fetch execution results for this scenario
            const results = await new Promise<any[]>((resolve, reject) => {
                this.executionResultService.loadExecutionResultsByScenario(scenario.id).subscribe({
                    next: (data) => resolve(data),
                    error: (err) => reject(err)
                });
            });
            
            if (!results || results.length === 0) {
                this.toastService.warn('No execution results found for this scenario');
                return;
            }

            // Group results by execution session
            const groupedBySession = results.reduce((acc, result) => {
                const sessionKey = result.startedAt ? new Date(result.startedAt).toISOString().split('T')[0] : 'unknown';
                if (!acc[sessionKey]) {
                    acc[sessionKey] = [];
                }
                acc[sessionKey].push(result);
                return acc;
            }, {} as Record<string, any[]>);

            // Get the most recent session
            const sessions = Object.keys(groupedBySession).sort().reverse();
            const latestSessionResults = groupedBySession[sessions[0]];

            // Sort by step sequence to ensure correct ordering
            latestSessionResults.sort((a: any, b: any) => {
                const seqA = a.stepSequence || a.sequenceNumber || 0;
                const seqB = b.stepSequence || b.sequenceNumber || 0;
                return seqA - seqB;
            });

            // Calculate counts, preferring live executionStatus totals for accuracy
            const liveStatus = this.executionStatus()[scenario.id];
            const computedPassed = latestSessionResults.filter((r: any) => r.status === 'success' || r.status === 'passed').length;
            const computedFailed = latestSessionResults.filter((r: any) => r.status === 'failed' || r.status === 'error').length;
            const totalFromStatus = liveStatus?.total ?? undefined;
            const passedFromStatus = liveStatus?.passed ?? undefined;
            const failedFromStatus = liveStatus?.failed ?? undefined;

            const passedCount = typeof passedFromStatus === 'number' ? passedFromStatus : computedPassed;
            const failedCount = typeof failedFromStatus === 'number' ? failedFromStatus : computedFailed;
            const totalCount = typeof totalFromStatus === 'number' ? totalFromStatus : (computedPassed + computedFailed);

            // Get failed step details with correct step numbers from actual step sequence
            const failedSteps = latestSessionResults
                .map((r: any, index: number) => ({
                    result: r,
                    actualStepNumber: r.stepSequence || r.sequenceNumber || (index + 1)
                }))
                .filter((item: any) => item.result.status === 'failed' || item.result.status === 'error')
                .map((item: any) => ({
                    stepNumber: item.actualStepNumber,
                    error: item.result.logText || item.result.statusText || item.result.testDescription || 'Unknown error'
                }));

            const passed = failedCount === 0;

            // Pre-compute summary data to avoid template calculations
            const summary = {
                totalScenarios: 1,
                passed: passed ? 1 : 0,
                failed: passed ? 0 : 1,
                successRate: passed ? 100 : 0
            };

            // Set the result data with pre-computed values
            this.resultDialogData.set({
                scenario: scenario,
                summary: summary,
                results: [{
                    scenarioId: scenario.scenarioId,
                    scenarioName: scenario.scenarioName,
                    totalCount: totalCount,
                    passedCount: passedCount,
                    failedCount: failedCount,
                    passed: passed,
                    failedSteps: failedSteps
                }]
            });

            // Data ready (dialog already open)
        } catch (error) {
            console.error('Error loading execution results:', error);
            this.toastService.error('Failed to load execution results');
            // Keep dialog open with an error state
            this.resultDialogData.set({
                scenario: scenario,
                summary: { totalScenarios: 1, passed: 0, failed: 1, successRate: 0 },
                results: []
            });
        }
    }

    // Close result dialog
    closeResultDialog(): void {
        this.resultDialog.set(false);
        this.resultDialogData.set(null);
    }

    // View aggregated results for multi-scenario execution using in-memory statuses
    viewMultiExecutionResults(): void {
        const queue = this.multiExecutionQueue();
        const statuses = this.executionStatus();

        const results = queue
            .map(s => ({ scenario: s, status: statuses[s.id] }))
            .filter(item => !!item.status && (item.status.total || 0) > 0)
            .map(item => {
                const passed = (item.status.failed || 0) === 0;
                const total = item.status.total || ((item.status.passed || 0) + (item.status.failed || 0));
                return {
                    scenarioId: item.scenario.scenarioId,
                    scenarioName: item.scenario.scenarioName,
                    totalCount: total,
                    passedCount: item.status.passed || 0,
                    failedCount: item.status.failed || 0,
                    passed: passed
                };
            });

        if (results.length === 0) {
            // fallback: if nothing to show, keep the simple dialog open with a message
            this.resultDialogData.set({
                scenario: null,
                summary: { totalScenarios: 0, passed: 0, failed: 0, successRate: 0 },
                results: []
            });
            this.resultDialog.set(true);
            return;
        }

        const passedScenarioCount = results.filter(r => r.failedCount === 0).length;
        const totalScenarios = results.length;
        const failedScenarioCount = totalScenarios - passedScenarioCount;
        const successRate = totalScenarios > 0 ? Math.round((passedScenarioCount / totalScenarios) * 100) : 0;

        this.resultDialogData.set({
            scenario: null,
            summary: {
                totalScenarios,
                passed: passedScenarioCount,
                failed: failedScenarioCount,
                successRate
            },
            results
        });

        this.resultDialog.set(true);
    }

    onDragStart(event: DragEvent, index: number): void {
        this.draggedScenarioIndex = index;
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/html', '');
        }
    }

    onDragOver(event: DragEvent, index: number): void {
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }
        this.dropTargetIndex = index;
    }

    onDrop(event: DragEvent, dropIndex: number): void {
        event.preventDefault();
        const dragIndex = this.draggedScenarioIndex;
        
        if (dragIndex !== -1 && dragIndex !== dropIndex) {
            const queue = [...this.multiExecutionQueue()];
            const draggedItem = queue[dragIndex];
            queue.splice(dragIndex, 1);
            queue.splice(dropIndex, 0, draggedItem);
            this.multiExecutionQueue.set(queue);
        }
        
        this.draggedScenarioIndex = -1;
        this.dropTargetIndex = -1;
    }

    onDragEnd(): void {
        this.draggedScenarioIndex = -1;
        this.dropTargetIndex = -1;
    }

    async executeMultipleScenarios(): Promise<void> {
        const queue = this.multiExecutionQueue();
        if (queue.length === 0) {
            this.toastService.error('No scenarios in execution queue');
            return;
        }

        // Cloud path: enqueue all scenarios in one batch
        console.log('[ExecuteMultiple] executeOnCloud =', this.executeOnCloud());
        if (this.executeOnCloud()) {
            const scenarioIds = queue.map(s => s.id).filter((id): id is string => !!id);
            if (scenarioIds.length === 0) {
                this.toastService.error('No valid scenarios to enqueue');
                return;
            }

            this.toastService.info(`Enqueuing ${scenarioIds.length} scenarios for cloud execution`);

            this.executionService.enqueueCloudScenarioRun(scenarioIds).subscribe({
                next: (res) => {
                    const r: any = res;
                    const jobId: string = r?.result?.jobId || r?.jobId || '';
                    if (jobId) {
                        this.addPendingJobId(jobId);
                        if (!this.jobPollingTimer) this.startJobPollingFromLocalStorage();
                    }
                    this.toastService.success(`${scenarioIds.length} scenarios enqueued for cloud execution`);
                },
                error: (err) => {
                    this.toastService.error(err?.error?.message || 'Failed to enqueue cloud runs');
                }
            });
            return;
        }

        // Start execution in background to avoid blocking UI
        setTimeout(async () => {
            this.isMultiExecuting.set(true);
            this.currentMultiExecutionIndex.set(0);

            // Await the batch jobId so execution results correlate with notification
            let resolvedBatchJobId: string | null = null;
            if (this.pendingMultiBatchJobIdPromise) {
                resolvedBatchJobId = await this.pendingMultiBatchJobIdPromise;
            }
            const batchExecutionId = resolvedBatchJobId || this.currentJob()?.jobId || crypto.randomUUID();
            const reportPayloads: ReportPayload[] = [];

            // Two-level session policy resolution: resolve all policies upfront
            this.toastService.info(`Resolving session policies for ${queue.length} scenario(s)...`);
            for (const scenario of queue) {
                if (scenario.id) {
                    const effective = await this.resolveEffectiveSessionPolicy(scenario.id, (scenario as any).sessionPolicy || null);
                    (scenario as any)._effectivePolicy = effective;
                    if (!(scenario as any).sessionPolicy && effective === 'FRESH') {
                        console.log(`[Local] Auto-detected FRESH for scenario ${scenario.scenarioName} (login steps found)`);
                    }
                }
            }

            // Build dependency chains for parallel execution
            const chains = this.buildLocalScenarioChains(queue);
            console.trace(`[Local] ${queue.length} scenarios → ${chains.length} chain(s) — STACK TRACE:`);

            if (chains.length > 1) {
                // PARALLEL: Execute independent chains concurrently
                // Each chain has isolated session keys (chainSuffix) and explicit session passing
                this.toastService.info(`Starting parallel execution: ${chains.length} chain(s), ${queue.length} scenarios`);
                const chainPromises = chains.map((chain, ci) => this.executeChainLocally(chain, ci, batchExecutionId, reportPayloads));
                await Promise.all(chainPromises);
            } else {
                // SEQUENTIAL: Single chain — no isolation needed
                this.toastService.info(`Starting execution of ${queue.length} scenarios`);
                for (let i = 0; i < queue.length; i++) {
                    if (!this.isMultiExecuting()) {
                        // Mark remaining (un-executed) scenarios as not started — don't save reports for them
                        for (let j = i; j < queue.length; j++) {
                            const remaining = queue[j];
                            const rSid = remaining.id;
                            if (rSid) {
                                this.setExecutionStatus(rSid, { running: false, current: 0, total: 0 });
                            }
                        }
                        this.toastService.info('Multi-scenario execution stopped');
                        break;
                    }
                    this.currentMultiExecutionIndex.set(i);
                    const scenario = queue[i];
                    if ((scenario as any)._effectivePolicy) {
                        (scenario as any).sessionPolicy = (scenario as any)._effectivePolicy;
                    }
                    this.toastService.info(`Executing scenario ${i + 1}/${queue.length}: ${scenario.scenarioName}`);
                    await this.executeSingleScenarioAsync(scenario, batchExecutionId, reportPayloads);
                    if (i < queue.length - 1 && this.isMultiExecuting()) {
                        await this.delay(this.interScenarioDelay());
                    }
                }
            }

            // Save all reports in a single API call
            if (reportPayloads.length > 0) {
                this.reportApiService.bulkSaveReports(reportPayloads).subscribe({
                    next: () => {},
                    error: (err) => {
                        console.error('[ExecutionsVM] Failed to save batch reports:', err);
                    }
                });
            }

            this.isMultiExecuting.set(false);

            // Update cloud job status if this was a cloud-registered batch
            // Await the enqueue promise in case the HTTP response hasn't arrived yet
            if (this.pendingMultiBatchJobIdPromise) {
                const batchJobId = await this.pendingMultiBatchJobIdPromise;
                this.pendingMultiBatchJobIdPromise = null;
                this.pendingMultiBatchJobId = null;
                if (batchJobId) {
                    try {
                        await firstValueFrom(this.executionService.updateJobStatus(batchJobId, 'completed'));
                        console.log('[Notification] Multi: updateJobStatus completed for', batchJobId);
                    } catch (e) {
                        console.error('[Notification] Multi: updateJobStatus FAILED:', e);
                    }
                }
            } else if (this.pendingMultiBatchJobId) {
                try {
                    await firstValueFrom(this.executionService.updateJobStatus(this.pendingMultiBatchJobId, 'completed'));
                    console.log('[Notification] Multi: updateJobStatus completed for', this.pendingMultiBatchJobId);
                } catch (e) {
                    console.error('[Notification] Multi: updateJobStatus FAILED:', e);
                }
                this.pendingMultiBatchJobId = null;
            }

            // Keep currentMultiExecutionIndex at last completed scenario index instead of resetting to -1
            // This allows the UI to show completed status correctly

            // Cleanup: Destroy all pooled sessions after multi-execution completes
            await this.sessionPoolService.destroyAll();

            this.toastService.success(`Completed execution of ${queue.length} scenarios`);
        }, 100);
    }

    /**
     * Group scenarios into dependency chains for local execution.
     * Uses _effectivePolicy (resolved by resolveEffectiveSessionPolicy).
     */
    private buildLocalScenarioChains(scenarios: Scenario[]): Scenario[][] {
        const chains: Scenario[][] = [];
        const chainsByAuth = new Map<string, Scenario[]>();
        let lastChain: Scenario[] | null = null;

        for (const scenario of scenarios) {
            const authKey = this.resolveAuthProfileId(scenario as any);
            const policy = (scenario as any)._effectivePolicy || (scenario as any).sessionPolicy;

            if (policy === 'REUSE') {
                if (authKey && chainsByAuth.has(authKey)) {
                    chainsByAuth.get(authKey)!.push(scenario);
                    lastChain = chainsByAuth.get(authKey)!;
                } else if (lastChain) {
                    lastChain.push(scenario);
                } else {
                    const chain = [scenario];
                    chains.push(chain);
                    lastChain = chain;
                    if (authKey) chainsByAuth.set(authKey, chain);
                }
            } else {
                const chain = [scenario];
                chains.push(chain);
                lastChain = chain;
                if (authKey) chainsByAuth.set(authKey, chain);
            }
        }
        return chains;
    }

    /**
     * Execute a single chain of scenarios locally (sequentially within the chain).
     */
    private async executeChainLocally(chain: Scenario[], chainIndex: number, batchExecutionId?: string, reportPayloads?: ReportPayload[]): Promise<void> {
        console.log(`[Chain ${chainIndex}] Starting with ${chain.length} scenario(s)`);
        let previousSession: any = null;
        for (let i = 0; i < chain.length; i++) {
            if (!this.isMultiExecuting()) break;
            const scenario = chain[i];
            if ((scenario as any)._effectivePolicy) {
                (scenario as any).sessionPolicy = (scenario as any)._effectivePolicy;
            }
            console.log(`[Chain ${chainIndex}] Executing ${scenario.scenarioName} (${i + 1}/${chain.length})`);
            const result = await this.executeSingleScenarioAsync(scenario, batchExecutionId, reportPayloads, { chainId: chainIndex, previousSession });
            previousSession = result.session || previousSession;

            if (i < chain.length - 1 && this.isMultiExecuting()) {
                await this.delay(this.interScenarioDelay());
            }
        }
        console.log(`[Chain ${chainIndex}] Completed`);
    }

    stopMultiExecution(): void {
        // Flag all queued scenarios as stopping so no reports saved for un-executed ones
        const queue = this.multiExecutionQueue();
        if (queue) {
            for (const s of queue) {
                if (s.id) this.setStoppingFlag(s.id);
            }
        }
        this.isMultiExecuting.set(false);
        this.toastService.info('Stopping multi-scenario execution after current scenario...');
    }

    // Stop current scenario immediately and halt multi execution
    stopCurrentMultiExecution(): void {
        try {
            if (this.isMultiExecuting()) {
                const queue = this.multiExecutionQueue();
                // Flag ALL scenarios in the queue as stopping so parallel chains also skip report saving
                if (queue) {
                    for (const s of queue) {
                        if (s.id) this.setStoppingFlag(s.id);
                    }
                }
                const idx = this.currentMultiExecutionIndex();
                const current = queue && idx >= 0 ? queue[idx] : null;
                if (current) {
                    this.stopScenario(current);
                }
                this.isMultiExecuting.set(false);
                this.toastService.warn('Execution stopped');
            }
        } catch (e) {
            this.isMultiExecuting.set(false);
            this.toastService.error('Failed to stop execution');
        }
    }

    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private buildReportPayload(
        sc: any,
        scenarioId: string,
        report: { totalSteps: number; passed: number; failed: number },
        executionId: string,
        stopped?: boolean
    ): ReportPayload | null {
        try {
            const clientId = sc?.functionalArea?.clientPackageMapping?.clientId || null;
            const packageId = sc?.functionalArea?.clientPackageMapping?.packageId || null;
            const clientPackageMappingId = sc?.functionalArea?.clientPackageMapping?.id || null;
            const functionalAreaId = sc?.functionalAreaId || null;

            const statusId = stopped ? this.reportStatusStoppedId
                : report.failed > 0 ? this.reportStatusFailedId : this.reportStatusPassedId;

            const testTypeId = sc?.testTypeId || sc?.functionalArea?.testType?.id || null;

            return {
                clientId,
                packageId,
                clientPackageMappingId,
                functionalAreaId,
                testTypeId,
                scenarioId,
                executionId,
                tenantId: this.selectedTenantId() || undefined,
                numberOfSteps: report.totalSteps,
                passed: report.passed,
                failed: report.failed,
                status: statusId || undefined,
                executionStatus: statusId || undefined,
            };
        } catch (err) {
            console.error('[ExecutionsVM] Error building report payload:', err);
            return null;
        }
    }

    private saveReportAfterExecution(
        sc: any,
        scenarioId: string,
        report: { totalSteps: number; passed: number; failed: number },
        executionId?: string,
        stopped?: boolean
    ): void {
        const eid = executionId || this.currentJob()?.jobId || crypto.randomUUID();
        const payload = this.buildReportPayload(sc, scenarioId, report, eid, stopped);
        if (!payload) return;

        this.reportApiService.bulkSaveReports([payload]).subscribe({
            next: () => {},
            error: (err) => {
                console.error('[ExecutionsVM] Failed to save report:', err);
            }
        });
    }

    private executeSingleScenarioAsync(scenario: Scenario, batchExecutionId?: string, reportPayloads?: ReportPayload[], chainOptions?: { chainId?: number; previousSession?: any }): Promise<{ session?: any }> {
        const chainId = chainOptions?.chainId;
        const chainSuffix = chainId != null ? `-chain${chainId}` : '';
        return new Promise((resolve) => {
            const sid = scenario.id;
            if (!sid) {
                this.toastService.error('Invalid scenario id');
                resolve({});
                return;
            }

            this.clearExecutionStatus(sid);
            this.clearStoppingFlag(sid);

            this.executionService.getScenarioById(sid).subscribe({
                next: async (sc: any) => {
                    const initialUrl = this.resolveExecutionUrl(sc?.url);
                    const functionalAreaId = sc?.functionalAreaId || '';
                    const clientPackageMappingId = sc?.functionalArea?.clientPackageMapping?.id || '';
                    const executionRunId = batchExecutionId || uuidv4();
                    const executionContext = { functionalAreaId, scenarioId: sid, clientPackageMappingId, executionRunId, tenantId: this.selectedTenantId() || undefined };

                    // Set context — use singleton only when not in parallel chain mode
                    if (chainId == null) {
                        this.executionResultService.setExecutionContext(functionalAreaId, sid, clientPackageMappingId, this.selectedTenantId() || undefined);
                        this.executionResultService.startSession(functionalAreaId, sid, clientPackageMappingId, this.selectedTenantId() || undefined);
                    }

                    // Check if preconditions are required (same as runScenario)
                    const resolvedAuthProfileId = this.resolveAuthProfileId(sc);
                    const hasPreconditions = resolvedAuthProfileId || sc?.screenId;

                    // Check if a session is already active from previous scenario
                    // In chain mode, use explicit previousSession; otherwise use singleton
                    const existingSession = chainId != null ? chainOptions?.previousSession : this.testExecutorService.getSession();

                    // Only reuse existing session if current scenario has NO auth profile
                    // If scenario has auth profile, it needs its own fresh session with its own login
                    const canReuseSession = existingSession && !resolvedAuthProfileId && sc?.sessionPolicy !== 'FRESH';

                    // CRITICAL: Always use session windows instead of legacy BrowserView
                    // Session windows are persistent and don't get destroyed when user switches tabs
                    const useSessionWindow = true; // hasPreconditions || canReuseSession || true (always use session)

                    const bridge: any = (window as any).windowHost;
                    const hostBridge = (window as any).host;
                    
                    if (!bridge || (!bridge.createHiddenHost && !hostBridge?.createSessionWindow)) {
                        resolve({});
                        return;
                    }

                    let sessionHandle: number | null = null;
                    let sessionState: any = null;
                    
                    try {
                        if (useSessionWindow) {
                            // Use session window (always - more stable than BrowserView)
                            // Session windows persist even when user switches tabs/views
                            
                            if (hasPreconditions) {
                                // Scenario has its own preconditions - acquire/create session
                                const sessionPolicy = sc.sessionPolicy || 'REUSE';
                                try {
                                    sessionState = await this.sessionPoolService.acquireSession(
                                        {
                                            authProfileId: resolvedAuthProfileId! + chainSuffix,
                                            tenantId: sc.tenantId
                                        },
                                        sessionPolicy
                                    );
                                    sessionHandle = sessionState.windowHandle;
                                } catch (sessionError: any) {
                                    this.toastService.error(`Session acquisition failed: ${sessionError.message}`);
                                    resolve({});
                                    return;
                                }

                                // Execute preconditions (login + screen navigation)
                                try {
                                    this.toastService.info(`Executing ${scenario.scenarioName}: Setting up preconditions...`);
                                    
                                    // Initialize session pool with scenario's screen master
                                    const screens = sc.screenMaster ? [sc.screenMaster] : [];
                                    // Ensure screen has screenId field matching scenario.screenId
                                    if (screens.length > 0 && sc.screenId) {
                                      screens[0].screenId = sc.screenId;
                                    }
                                    await this.sessionPoolService.initialize([], screens);
                                    
                                    const preconditionResult = await this.sessionPoolService.executePreconditions(
                                        sc,
                                        sessionState,
                                        []
                                    );

                                    if (!preconditionResult.success) {
                                        this.toastService.error(`Precondition failed: ${preconditionResult.error}`);
                                        if (sessionHandle) {
                                            await hostBridge.destroySessionWindow(sessionHandle);
                                        }
                                        resolve({});
                                        return;
                                    }

                                    this.toastService.success(`${scenario.scenarioName}: Preconditions completed`);
                                    
                                    // Log precondition execution to database
                                    this.executionResultService.logPreconditionExecution(preconditionResult, executionContext);

                                    // Set current session for test executor (skip singleton in chain mode)
                                    if (chainId == null) this.testExecutorService.setSession(sessionState);

                                } catch (preconditionError: any) {
                                    this.toastService.error(`Precondition failed: ${preconditionError?.message || preconditionError}`);
                                    // Don't destroy session on precondition failure - may be reusable
                                    resolve({});
                                    return;
                                }
                            } else if (canReuseSession) {
                                // Reusing existing session from previous scenario
                                // CRITICAL: Validate window handle is still valid before reuse
                                const windowHandle = existingSession?.windowHandle;
                                if (windowHandle) {
                                    const validation = await hostBridge.validateSessionWindow(windowHandle);
                                    
                                    if (!validation?.valid) {
                                        this.toastService.warn(`Session window ${windowHandle} was destroyed, creating new session`);
                                        
                                        // Clear stale session and acquire fresh one
                                        this.testExecutorService.setSession(null);

                                        try {
                                            sessionState = await this.sessionPoolService.acquireSession(
                                                {
                                                    authProfileId: (resolvedAuthProfileId || 'default') + chainSuffix,
                                                    tenantId: sc.tenantId
                                                },
                                                'REUSE'
                                            );
                                            sessionHandle = sessionState.windowHandle;
                                            if (chainId == null) this.testExecutorService.setSession(sessionState);
                                            this.toastService.info(`${scenario.scenarioName}: Using fresh session (window ${sessionHandle})`);

                                            // Navigate fresh session to scenario URL if provided
                                            if (initialUrl && initialUrl !== 'about:blank') {
                                                try {
                                                    await hostBridge.navigateSession(sessionHandle, initialUrl);
                                                    await this.delay(2000); // Wait for navigation to complete
                                                    this.toastService.info(`Navigated to ${initialUrl}`);
                                                } catch (navError: any) {
                                                    this.toastService.error(`Navigation failed: ${navError.message || navError}`);
                                                }
                                            }
                                        } catch (sessionError: any) {
                                            this.toastService.error(`Session acquisition failed: ${sessionError.message}`);
                                            resolve({ session: sessionState });
                                            return;
                                        }
                                    } else {
                                        sessionState = existingSession;
                                        sessionHandle = windowHandle;
                                        this.toastService.info(`${scenario.scenarioName}: Using existing session (already logged in)`);
                                        
                                        // CRITICAL: Ensure session is set in executor so steps use session window
                                        this.testExecutorService.setSession(sessionState);

                                        // DO NOT navigate to initialUrl when reusing a logged-in session.
                                        // The session is already on the correct tenant/app page.
                                        // Navigating to sc.url (typically the login/tenant URL) would
                                        // reset the session and show the login page again.
                                        // The test steps themselves will handle any in-app navigation.
                                    }
                                } else {
                                    // No window handle, treat as fresh session needed
                                        
                                        // Navigate fresh session to scenario URL if provided
                                        if (initialUrl && initialUrl !== 'about:blank') {
                                            try {
                                                await hostBridge.navigateSession(sessionHandle, initialUrl);
                                                await this.delay(2000); // Wait for navigation to complete
                                                this.toastService.info(`Navigated to ${initialUrl}`);
                                            } catch (navError: any) {
                                                this.toastService.error(`Navigation failed: ${navError.message || navError}`);
                                            }
                                        }
                                    this.testExecutorService.setSession(null);
                                    
                                    try {
                                        sessionState = await this.sessionPoolService.acquireSession(
                                            {
                                                authProfileId: (resolvedAuthProfileId || 'default') + chainSuffix,
                                                tenantId: sc.tenantId
                                            },
                                            'REUSE'
                                        );
                                        sessionHandle = sessionState.windowHandle;
                                        this.testExecutorService.setSession(sessionState);
                                        this.toastService.info(`${scenario.scenarioName}: Using fresh session (window ${sessionHandle})`);
                                    } catch (sessionError: any) {
                                        this.toastService.error(`Session acquisition failed: ${sessionError.message}`);
                                        resolve({});
                                        return;
                                    }
                                }
                            } else {
                                // No preconditions AND no existing session - create fresh session window
                                this.testExecutorService.setSession(null);
                                
                                try {
                                    const elseSessionPolicy = sc?.sessionPolicy === 'FRESH' ? 'FRESH' : 'REUSE';
                                    sessionState = await this.sessionPoolService.acquireSession(
                                        {
                                            authProfileId: 'no-auth' + chainSuffix,
                                            tenantId: sc.tenantId || 'default'
                                        },
                                        elseSessionPolicy
                                    );
                                    sessionHandle = sessionState.windowHandle;
                                    this.testExecutorService.setSession(sessionState);
                                    this.toastService.info(`${scenario.scenarioName}: Using fresh session (window ${sessionHandle})`);
                                    
                                    // Navigate fresh session to scenario URL if provided
                                    if (initialUrl && initialUrl !== 'about:blank') {
                                        try {
                                            await hostBridge.navigateSession(sessionHandle, initialUrl);
                                            await this.delay(2000); // Wait for navigation to complete
                                            this.toastService.info(`Navigated to ${initialUrl}`);
                                        } catch (navError: any) {
                                            this.toastService.error(`Navigation failed: ${navError.message || navError}`);
                                        }
                                    }
                                } catch (sessionError: any) {
                                    this.toastService.error(`Session acquisition failed: ${sessionError.message}`);
                                    resolve({});
                                    return;
                                }
                            }
                        } else {
                            // LEGACY PATH - Should never reach here now
                            // Use legacy hidden host for scenarios without preconditions
                            // CRITICAL: Clear any existing session from previous scenarios
                            this.testExecutorService.setSession(null);
                            
                            const res = await bridge.createHiddenHost({ initialUrl });
                            if (!res?.success) {
                                this.toastService.error(res?.error || 'Failed to create hidden host');
                                resolve({});
                                return;
                            }

                            // CRITICAL: Wait for hidden host to be fully ready before accessing APIs
                            // This prevents "Browser view not ready" errors in continuous execution
                            await this.delay(500);

                            // Clear session
                            if (hostBridge && hostBridge.clearSession) {
                                await hostBridge.clearSession();
                            }

                            // Navigate if URL is provided
                            if (initialUrl && initialUrl !== 'about:blank') {
                                await this.delay(2000);
                                
                                if (!hostBridge || !hostBridge.navigate) {
                                    this.toastService.error('Navigation API not available');
                                    if (hostBridge && hostBridge.destroyHiddenHost) await hostBridge.destroyHiddenHost();
                                    resolve({});
                                    return;
                                }
                                
                                try {
                                    const navRes = await hostBridge.navigate(initialUrl);
                                    
                                    // Some navigate APIs don't return a value, treat undefined as success
                                    if (navRes === undefined || navRes?.success !== false) {
                                    } else if (navRes?.success === false) {
                                        this.toastService.error(`Navigation failed: ${navRes?.error || 'Unknown error'}`);
                                        if (hostBridge.destroyHiddenHost) await hostBridge.destroyHiddenHost();
                                        resolve({});
                                        return;
                                    }
                                } catch (navError: any) {
                                    this.toastService.error(`Navigation error: ${navError.message || navError}`);
                                    if (hostBridge.destroyHiddenHost) await hostBridge.destroyHiddenHost();
                                    resolve({});
                                    return;
                                }
                                
                                await this.delay(3000);
                            }
                        }

                        this.stepsService.getStepsByScenario(sid).subscribe({
                            next: async (res: any) => {
                                const stepsArray: any[] = Array.isArray(res) ? res : (res && Array.isArray(res.data)) ? res.data : [];
                                const active = stepsArray.filter(s => s.isActive !== false).sort((a, b) => (a.stepId || 0) - (b.stepId || 0));
                                
                                if (active.length === 0) {
                                    this.toastService.error('No active steps found');
                                    if (hostBridge && hostBridge.destroyHiddenHost) await hostBridge.destroyHiddenHost();
                                    resolve({});
                                    return;
                                }

                                this.setExecutionStatus(sid, { running: true, current: 0, total: active.length });

                                const stepInput = active.map(s => {
                                    const addInfo = this.stepPlaceholderService.parseAdditionalInfo(s.additionalInfo || '{}');
                                    return s.placeholder && s.placeholder.trim()
                                        ? this.stepPlaceholderService.resolvePlaceholder(s.placeholder, addInfo)
                                        : s.stepDescription;
                                }).join('\n');

                                const stepMetadata = active.map(s => {
                                    const addInfo = this.stepPlaceholderService.parseAdditionalInfo(s.additionalInfo || '{}');
                                    return {
                                        stepId: s.stepId,
                                        stepUuid: s.id,
                                        stepDescription: (s.placeholder && s.placeholder.trim())
                                          ? this.stepPlaceholderService.resolvePlaceholder(s.placeholder, addInfo)
                                          : s.stepDescription,
                                        isRecording: s.isRecording || false,
                                        additionalInfo: addInfo
                                    };
                                });

                                try {
                                    const report = await this.testExecutorService.executeScenario(stepInput, {
                                        stopOnFailure: false,
                                        stepMetadata,
                                        session: chainId != null ? sessionState : undefined,
                                        executionContext,
                                        onProgress: (current, total) => {
                                            if (!this.isStopping(sid)) {
                                                this.setExecutionStatus(sid, {
                                                    running: true,
                                                    current,
                                                    total
                                                });
                                            }
                                        }
                                    });

                                    const passed = report?.passed || 0;
                                    const failed = report?.failed || 0;

                                    // Update final status with pass/fail counts
                                    if (!this.isStopping(sid)) {
                                        this.setExecutionStatus(sid, {
                                            running: false,
                                            current: active.length,
                                            total: active.length,
                                            passed,
                                            failed
                                        });
                                    } else {
                                        // Scenario was stopped — update status to show stopped state
                                        const prev = this.executionStatus()[sid];
                                        this.setExecutionStatus(sid, {
                                            running: false,
                                            current: prev?.current || 0,
                                            total: active.length,
                                            passed,
                                            failed
                                        });
                                    }
                                    // Save report record for this scenario (flag as stopped if user requested stop)
                                    const wasStopped = this.isStopping(sid);
                                    if (batchExecutionId && reportPayloads) {
                                        const payload = this.buildReportPayload(sc, sid, {
                                            totalSteps: report?.totalSteps || active.length,
                                            passed,
                                            failed
                                        }, batchExecutionId, wasStopped);
                                        if (payload) reportPayloads.push(payload);
                                    } else {
                                        this.saveReportAfterExecution(sc, sid, {
                                            totalSteps: report?.totalSteps || active.length,
                                            passed,
                                            failed
                                        }, undefined, wasStopped);
                                    }
                                } catch (execError) {
                                    // Ensure we clear running flag on failures
                                    if (!this.isStopping(sid)) {
                                        const prev = this.executionStatus()[sid];
                                        this.setExecutionStatus(sid, {
                                            running: false,
                                            current: prev?.current || 0,
                                            total: active.length,
                                            passed: prev?.passed || 0,
                                            failed: (prev?.failed || 0) + 1
                                        });
                                    }
                                }

                                // CRITICAL: Wait for all pending saves to complete before destroying host
                                try {
                                    await this.executionResultService.waitForPendingSaves();
                                } catch {}

                                try {
                                    // DON'T destroy session window here - keep it alive for next scenarios
                                    // Session cleanup happens at end of multi-execution via sessionPoolService.destroyAll()
                                    // Only destroy hidden host (legacy mode) as it's not reusable
                                    if (!useSessionWindow && hostBridge && hostBridge.destroyHiddenHost) {
                                        await hostBridge.destroyHiddenHost();
                                        await this.delay(300);
                                    }
                                } finally {
                                    if (functionalAreaId) {
                                        this.executionResultService.endSession();
                                    }
                                    resolve({ session: sessionState });
                                }
                            },
                            error: async () => {
                                this.toastService.error('Failed to load steps');
                                // Update status to show error
                                if (!this.isStopping(sid)) {
                                    this.setExecutionStatus(sid, {
                                        running: false,
                                        current: 0,
                                        total: 0,
                                        passed: 0,
                                        failed: 1
                                    });
                                }
                                // On error, only destroy hidden host, keep session window for potential reuse
                                if (!useSessionWindow && hostBridge && hostBridge.destroyHiddenHost) {
                                    await hostBridge.destroyHiddenHost();
                                    await this.delay(300);
                                }
                                resolve({});
                            }
                        });
                    } catch (error) {
                        this.toastService.error('Execution failed');
                        // Update status to show error
                        if (!this.isStopping(sid)) {
                            this.setExecutionStatus(sid, {
                                running: false,
                                current: 0,
                                total: 0,
                                passed: 0,
                                failed: 1
                            });
                        }
                        // CRITICAL: Only destroy session window on terminal/unrecoverable errors
                        // During multi-execution, keep windows alive for reuse by subsequent scenarios
                        // Session cleanup happens at the end via sessionPoolService.destroyAll()
                        const isMultiExecution = this.multiExecutionQueue()?.length > 0;
                        const errorMsg = (error as any)?.message || String(error);
                        const isTerminalError = errorMsg?.includes('Session acquisition failed') || 
                                               errorMsg?.includes('Failed to create');
                        
                        if (useSessionWindow && sessionHandle) {
                            if (isTerminalError && !isMultiExecution) {
                                // Only destroy on terminal errors in single execution mode
                                if (hostBridge?.destroySessionWindow) {
                                    await hostBridge.destroySessionWindow(sessionHandle);
                                }
                                await this.delay(300);
                            }
                        } else if (hostBridge && hostBridge.destroyHiddenHost) {
                            await hostBridge.destroyHiddenHost();
                            await this.delay(300);
                        }
                        resolve({});
                    }
                },
                error: () => {
                    this.toastService.error('Failed to load scenario');
                    resolve({});
                }
            });
        });
    }

    // Get status for the scenario at the current index even when not executing
    getStoppedScenarioProgress() {
        const idx = this.currentMultiExecutionIndex();
        const queue = this.multiExecutionQueue();
        if (idx < 0 || !queue || queue.length === 0) return null;
        const scenario = queue[idx];
        if (!scenario) return null;
        const status = this.executionStatus()[scenario.id];
        if (!status || status.running) return null;
        return status;
    }


    public getTypeTestOptions() {
        return this.type_test.map(item => ({
            value: item.referenceValue,
            id: item.id,
        }));
    }


    public getTestTypeOptions(): ReferenceData[] {
        return this.type_test;
    }





    public testTypeFilterOptions() {
        return this.type_test.map(item => ({
            label: item.referenceValue,
            value: item.id
        }));
    }
    public functionalAreaFilterOptions = computed(() => {
        // Return empty if test type not selected
        const selectedTestTypes = this.filterTestType();
        if (!selectedTestTypes || selectedTestTypes.length === 0) {
            return [];
        }
        
        const packageName = this.selectedPackageFilter();
        const allScenarios = this.allScenarios();

        // Filter scenarios by test types (multiple)
        let filteredScenarios = allScenarios.filter(scenario => 
            selectedTestTypes.includes(scenario.testTypeId)
        );

        // Further filter by package if selected
        if (packageName) {
            filteredScenarios = filteredScenarios.filter(scenario => {
                const clientPackage = scenario.functionalArea?.clientPackageMapping?.package?.packageName;
                const templatePackage = scenario.functionalArea?.package?.packageName;
                return clientPackage === packageName || templatePackage === packageName;
            });
        }

        // Extract unique functional areas from filtered scenarios
        const functionalAreasMap = new Map();
        for (const scenario of filteredScenarios) {
            if (scenario.functionalArea && scenario.functionalArea.id) {
                const area: any = { ...scenario.functionalArea };
                
                // Ensure testType is included
                if (!area.testType && scenario.testType) {
                    area.testType = scenario.testType;
                }
                if (!area.testTypeId && scenario.testTypeId) {
                    area.testTypeId = scenario.testTypeId;
                }
                
                if (!functionalAreasMap.has(area.id)) {
                    functionalAreasMap.set(area.id, area);
                }
            }
        }

        const uniqueAreas = Array.from(functionalAreasMap.values());

        return uniqueAreas.map((item: any) => {
            const testTypeName = item.testType?.referenceValue || item.testType?.name || '';
            const displayLabel = testTypeName 
                ? `${item.name || item.referenceValue} (${testTypeName})`
                : item.name || item.referenceValue;
            
            return {
                label: displayLabel,
                value: item.id
            };
        });
    });

    private startCloudExecutionPolling(scenarios: Scenario[]): void {
        if (this.cloudPollingInterval) clearInterval(this.cloudPollingInterval);
        
        this.cloudPollingInterval = setInterval(() => {
            this.pollCloudExecutionProgress(scenarios);
        }, 3000); // Poll every 3 seconds
    }

    private async pollCloudExecutionProgress(scenarios: Scenario[]): Promise<void> {
        let allComplete = true;
        let activeIndex = -1;
        
        for (let i = 0; i < scenarios.length; i++) {
            const scenario = scenarios[i];
            const sid = scenario.id;
            const expectedSteps = this.cloudExpectedSteps()[sid] || 0;
            
            try {
                const results: any = await new Promise((resolve, reject) => {
                    this.executionResultService.loadExecutionResultsByScenario(sid).subscribe({
                        next: (data) => resolve(data),
                        error: (err) => reject(err)
                    });
                });
                
                // Filter results from this execution (after enqueue time)
                const recentResults = (results || []).filter((r: any) => 
                    new Date(r.startedAt || r.createdAt).getTime() > this.cloudPollingStartTime
                );
                
                const current = recentResults.length;
                const passed = recentResults.filter((r: any) => r.status === 'success').length;
                const failed = recentResults.filter((r: any) => r.status === 'failed').length;
                
                const isComplete = current >= expectedSteps && expectedSteps > 0;
                const isRunning = current > 0 && !isComplete;
                
                // Update status
                this.setExecutionStatus(sid, {
                    running: !isComplete,
                    current,
                    total: expectedSteps,
                    passed,
                    failed
                });
                
                // Track which scenario is actively running
                if (isRunning && activeIndex < 0) {
                    activeIndex = i;
                }
                
                if (!isComplete) {
                    allComplete = false;
                }
            } catch (err) {
                console.error(`Failed to poll results for ${sid}:`, err);
            }
        }
        
        // Update current index to show active scenario
        if (activeIndex >= 0) {
            this.currentMultiExecutionIndex.set(activeIndex);
        } else if (allComplete) {
            this.currentMultiExecutionIndex.set(scenarios.length - 1);
        }
        
        // Stop polling when all complete
        if (allComplete && scenarios.length > 0) {
            this.stopCloudExecutionPolling();
            this.isMultiExecuting.set(false);
            this.toastService.success('Cloud execution completed');
        }
    }

    private stopCloudExecutionPolling(): void {
        if (this.cloudPollingInterval) {
            clearInterval(this.cloudPollingInterval);
            this.cloudPollingInterval = null;
        }
    }

    // === GROUP EXECUTE METHODS ===
    public setViewMode(mode: 'list' | 'group'): void {
        this.viewMode.set(mode);
        if (mode === 'group') {
            this.loadAllClientScenarios();
        }
    }

    private allClientScenariosSub: Subscription | null = null;

    private getFilterHash(clientId: string): string {
        return `${clientId}|${this.selectedPackageFilter()}|${this.selectedTestSuiteFilter()}|${this.selectedFunctionalAreaFilter()}|${this.filterTestType()}|${this.scenarioSearchText()}`;
    }

    private loadAllClientScenarios(force = false): void {
        const clientId = this.selectedClientFilter();
        if (!clientId) return;

        const filterHash = this.getFilterHash(clientId);
        if (!force && this.allClientScenariosFilterHash === filterHash && this.allClientScenarios().length > 0) {
            return;
        }

        if (this.allClientScenariosSub) {
            this.allClientScenariosSub.unsubscribe();
        }

        this.allClientScenariosLoading.set(true);
        this.allClientScenariosFilterHash = filterHash;
        this.allClientScenariosClientId = clientId;
        const filterPayload = this.buildScenarioFilterPayload(clientId);

        this.allClientScenariosSub = this.executionService.getGroupingSummary(filterPayload).subscribe({
            next: (response) => {
                const listScenarios = this.allScenarios();
                const scenarios = (response || []).map((s: any) => {
                    const listMatch = listScenarios.find((ls: any) => ls.id === s.id);
                    return {
                        id: s.id,
                        scenarioId: s.scenarioId || '',
                        scenarioName: s.scenarioName,
                        functionalArea: listMatch?.functionalArea || { name: s.functionalAreaName, referenceValue: s.functionalAreaName, clientPackageMappingId: s.clientPackageMappingId || null, clientPackageMapping: s.clientPackageMapping || null },
                        testType: { referenceValue: s.testTypeName },
                        stepCount: s.stepCount || 0,
                        selected: this.selectionMap.has(s.id),
                    };
                });
                this.allClientScenarios.set(scenarios);
                this.allClientScenariosLoading.set(false);
            },
            error: () => {
                this.allClientScenariosLoading.set(false);
                this.toastService.error('Failed to load grouping data');
            }
        });
    }

    public setGroupByField(field: 'functionalArea' | 'testType'): void {
        this.groupByField.set(field);
        // Reset toggle state when changing group field
        this.groupToggleState.set({});
    }

    public toggleGroup(groupName: string): void {
        const scenarios = this.getGroupScenarios(groupName);
        const enabledScenarios = scenarios.filter(s => s.stepCount && s.stepCount > 0 && !this.isScenarioPackageExpired(s));
        if (enabledScenarios.length === 0) return;

        const hasAnySelected = enabledScenarios.some(s => s.selected === true);
        const newState = !hasAnySelected;

        enabledScenarios.forEach(s => { s.selected = newState; });
        this.syncSelectionMap(scenarios);
        this.groupToggleState.set({ ...this.groupToggleState(), [groupName]: newState });

        if (!newState) {
            const expanded = this.expandedGroups();
            if (expanded[groupName]) {
                this.expandedGroups.set({ ...expanded, [groupName]: false });
            }
        }
    }

    public isGroupDisabled(groupName: string): boolean {
        const scenarios = this.getGroupScenarios(groupName);
        return scenarios.every(s => !s.stepCount || s.stepCount === 0 || this.isScenarioPackageExpired(s));
    }

    public selectAllGroups(): void {
        const state: Record<string, boolean> = {};
        const allGroupScenarios: any[] = [];
        this.groupExecuteGroups().forEach(g => {
            const hasExecutable = g.scenarios.some((s: any) => s.stepCount && s.stepCount > 0 && !this.isScenarioPackageExpired(s));
            state[g.name] = hasExecutable;
            g.scenarios.forEach((s: any) => {
                s.selected = s.stepCount && s.stepCount > 0 && !this.isScenarioPackageExpired(s);
            });
            allGroupScenarios.push(...g.scenarios);
        });
        this.syncSelectionMap(allGroupScenarios);
        this.groupToggleState.set(state);
    }

    public deselectAllGroups(): void {
        const state: Record<string, boolean> = {};
        const allGroupScenarios: any[] = [];
        this.groupExecuteGroups().forEach(g => {
            state[g.name] = false;
            g.scenarios.forEach(s => { s.selected = false; });
            allGroupScenarios.push(...g.scenarios);
        });
        this.syncSelectionMap(allGroupScenarios);
        this.groupToggleState.set(state);
        this.expandedGroups.set({});
        this.selectionMap.clear();
        this.allClientScenarios.set(this.allClientScenarios().map(s => ({ ...s, selected: false })));
    }

    public onScenarioSelectionChange(): void {
        if (this.viewMode() === 'group') {
            // In group view, sync from allClientScenarios
            const allClient = this.allClientScenarios();
            allClient.forEach((s: any) => {
                if (s.selected) {
                    this.selectionMap.set(s.id, s);
                } else {
                    this.selectionMap.delete(s.id);
                }
            });
            this.allClientScenarios.set([...allClient]);
        } else {
            this.syncSelectionMap();
            this.scenarios.set([...this.scenarios()]);
        }
        this.groupToggleState.set({ ...this.groupToggleState() });
    }

    private syncSelectionMap(additionalScenarios?: any[]): void {
        if (additionalScenarios) {
            // Group operations: only sync the provided scenarios
            additionalScenarios.forEach((s: any) => {
                if (s.selected) {
                    this.selectionMap.set(s.id, s);
                } else {
                    this.selectionMap.delete(s.id);
                }
            });
        } else {
            // List view checkbox: sync current page
            const scenarios = this.scenarios();
            scenarios.forEach((s: any) => {
                if (s.selected) {
                    this.selectionMap.set(s.id, s);
                } else {
                    this.selectionMap.delete(s.id);
                }
            });
        }
        // Sync selected state back to allClientScenarios for group view
        const allClient = this.allClientScenarios();
        if (allClient.length > 0) {
            let changed = false;
            allClient.forEach((s: any) => {
                const shouldBeSelected = this.selectionMap.has(s.id);
                if (s.selected !== shouldBeSelected) {
                    s.selected = shouldBeSelected;
                    changed = true;
                }
            });
            if (changed) {
                this.allClientScenarios.set([...allClient]);
            }
        }
    }

    private expandedGroups = signal<Record<string, boolean>>({});

    public toggleGroupExpand(groupName: string): void {
        const current = this.expandedGroups();
        const expanding = !current[groupName];
        if (expanding) {
            const scenarios = this.getGroupScenarios(groupName);
            scenarios.forEach(s => {
                if (s.selected === undefined) s.selected = false;
            });
        }
        this.expandedGroups.set({ ...current, [groupName]: expanding });
    }

    public isGroupExpanded(groupName: string): boolean {
        return !!this.expandedGroups()[groupName];
    }

    public getGroupScenarios(groupName: string): any[] {
        const group = this.groupExecuteGroups().find(g => g.name === groupName);
        return group?.scenarios || [];
    }

    public getGroupSelectedCount(groupName: string): number {
        const scenarios = this.getGroupScenarios(groupName);
        return scenarios.filter(s => s.selected === true).length;
    }

    private groupScenarioSearchMap: Record<string, string> = {};

    public getGroupScenarioSearch(groupName: string): string {
        return this.groupScenarioSearchMap[groupName] || '';
    }

    public setGroupScenarioSearch(groupName: string, value: string): void {
        this.groupScenarioSearchMap[groupName] = value;
    }

    public getFilteredGroupScenarios(groupName: string): any[] {
        const scenarios = this.getGroupScenarios(groupName);
        const search = this.getGroupScenarioSearch(groupName)?.toLowerCase();
        if (!search) return scenarios;
        return scenarios.filter(s =>
            s.scenarioName?.toLowerCase().includes(search) ||
            s.scenarioId?.toLowerCase().includes(search)
        );
    }

    public canExecuteGroups = computed(() => {
        if (this.enabledGroups().length === 0) return false;
        if (this.showTenantSelector() && !this.selectedTenantId()) return false;
        return true;
    });

    public executeGroups(): void {
        if (!this.canExecuteGroups()) {
            if (this.showTenantSelector() && !this.selectedTenantId()) {
                this.toastService.warn('Please select a tenant before executing');
            }
            return;
        }
        const enabled = this.enabledGroups();
        const scenarios = enabled.flatMap(g => g.scenarios).filter(s => !this.isScenarioPackageExpired(s) && !this.isScenarioAuthIncomplete(s));
        if (scenarios.length === 0) {
            this.toastService.warn('No executable scenarios in selected groups');
            return;
        }
        scenarios.forEach(s => s.selected = true);
        this.syncSelectionMap();
        this.viewMode.set('list');
        this.executeMultipleScenariosDirectly();
    }

    public getGroupIcon(groupName: string): string {
        const name = groupName.toLowerCase();
         if (name.includes('hcm') || name.includes('student')) return 'pi-users';
         if (name.includes('payroll') || name.includes('pay')) return 'pi-money-bill';
        // if (name.includes('benefit')) return 'pi-heart';
        // if (name.includes('security') || name.includes('sec')) return 'pi-shield';
        return 'pi-folder';
    }

    public getGroupIconBg(groupName: string): string {
        const name = groupName.toLowerCase();
        if (name.includes('hcm') || name.includes('human')) return '#d1fae5';
        if (name.includes('payroll') || name.includes('pay')) return '#dbeafe';
        if (name.includes('benefit')) return '#fce7f3';
        if (name.includes('security') || name.includes('sec')) return '#fef3c7';
        return '#f3f4f6';
    }

    public getGroupIconColor(groupName: string): string {
        const name = groupName.toLowerCase();
        if (name.includes('hcm') || name.includes('human')) return '#065f46';
        if (name.includes('payroll') || name.includes('pay')) return '#1e40af';
        if (name.includes('benefit')) return '#9d174d';
        if (name.includes('security') || name.includes('sec')) return '#92400e';
        return '#374151';
    }

    public cleanup(): void {
        this.stopCloudExecutionPolling();
        if (this.jobPollingTimer) {
            clearInterval(this.jobPollingTimer);
            this.jobPollingTimer = null;
        }
        this.scenariosSub?.unsubscribe();
        this.packagesSub?.unsubscribe();
        this.functionalAreasSub?.unsubscribe();
        // Clear session state to prevent stale window references when switching modes
        this.testExecutorService.setSession(null);
    }
}